import React, { useState, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
    Users,
    Search,
    Building2,
    Calendar,
    Shield,
    Eye,
    ArrowLeft,
    Mail,
    UserCheck,
    Hash,
    Crown,
    MapPin,
    Clock,
    Check,
    X,
    Plus,
    Trash2,
    Save,
    MessageSquare,
    AlertCircle,
    Target,
    FileText,
    Star,
    TrendingUp,
    TrendingDown,
    Award,
    ExternalLink,
    RefreshCw,
    Edit3,
    Copy,
    BarChart2,
    PenTool,
    AlertTriangle,
    Ban,
    Zap,
    Activity,
    LayoutGrid,
    Trophy,
    Medal,
    Flag,
    History,
    UserPlus
} from 'lucide-react';

import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogTrigger,
    DialogFooter
} from "@/components/ui/dialog";
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "@/components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useAuth } from '../../contexts/AuthContext';
import { theme } from '../../lib/theme';
import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { toast } from 'sonner';
import SkillRadar from './Profile/SkillRadar';
import ConductStatus from './Profile/ConductStatus';
import TimelineFeed from './Profile/TimelineFeed';
import { getStaffDisplayName } from '../../utils/staffDisplay';

const StaffReview = () => {
    const { allUsers, adminFullUpdateUser, user: currentUser } = useAuth();
    const [selectedCity, setSelectedCity] = useState(null);
    const [selectedStaff, setSelectedStaff] = useState(null);
    const [searchTerm, setSearchTerm] = useState('');

    // Editing State
    const [isEditing, setIsEditing] = useState(false);
    const [editForm, setEditForm] = useState({});
    const [alignmentsModalOpen, setAlignmentsModalOpen] = useState(false);
    const [notesModalOpen, setNotesModalOpen] = useState(false);

    // View State
    const [activeTab, setActiveTab] = useState('overview');

    // Feedback State
    const [selectedFeedbackMonth, setSelectedFeedbackMonth] = useState('');

    // New Item States
    const [newAlignment, setNewAlignment] = useState({ topic: '', actions: '', status: 'Pending' });
    const [newNote, setNewNote] = useState({ title: '', description: '', category: 'Geral' });
    const [showNewAlignmentForm, setShowNewAlignmentForm] = useState(false);

    // Launch Stats State
    const [launchModalOpen, setLaunchModalOpen] = useState(false);
    const [launchForm, setLaunchForm] = useState({ city: 'Capital', support: 0, coupon: 0, complaint: 0, other: 0 });
    const [isEditingNotes, setIsEditingNotes] = useState(false);
    const [isEditingFicha, setIsEditingFicha] = useState(false);

    // Helper: Gerar lista de meses (Atual e anteriores)
    // Level and XP Helpers
    const calculateLevel = () => {
        const xp = editForm.xp || 0;
        return Math.floor(Math.sqrt(xp / 100)) + 1;
    };

    const getXPProgress = () => {
        const xp = editForm.xp || 0;
        const currentLevel = calculateLevel();
        const nextLevelXP = Math.pow(currentLevel, 2) * 100;
        const currentLevelXP = Math.pow(currentLevel - 1, 2) * 100;
        const progress = ((xp - currentLevelXP) / (nextLevelXP - currentLevelXP)) * 100;
        return Math.min(100, Math.max(0, Math.round(progress)));
    };

    const getMonthOptions = () => {
        const options = [];
        const date = new Date();
        // Gerar últimos 12 meses + próximo
        for (let i = -1; i < 12; i++) {
            const d = new Date(date.getFullYear(), date.getMonth() - i, 1);
            const monthStr = d.toLocaleDateString('pt-BR', { month: 'long', year: 'numeric' });
            // Capitalize
            options.push(monthStr.charAt(0).toUpperCase() + monthStr.slice(1));
        }
        return options;
    };

    // Initialize edit form when staff is selected
    React.useEffect(() => {
        if (selectedStaff) {
            setEditForm({
                startDate: selectedStaff.staffDetails?.startDate || selectedStaff.joinDate || '',
                shift: selectedStaff.staffDetails?.shift || '',
                mainFunction: selectedStaff.staffDetails?.mainFunction || '',
                responsibilities: selectedStaff.staffDetails?.responsibilities || '',
                highlights: selectedStaff.staffDetails?.highlights || '',
                absentPeriod: selectedStaff.staffDetails?.absentPeriod || '',
                monthlyFeedback: selectedStaff.staffDetails?.monthlyFeedback || [],
                alignments: selectedStaff.staffDetails?.alignments || [],
                adminNotes: selectedStaff.staffDetails?.adminNotes || [],
                customCards: selectedStaff.staffDetails?.customCards || [],
                ticketStats: selectedStaff.staffDetails?.ticketStats || [],
                skills: selectedStaff.staffDetails?.skills || { comunicacao: 50, tecnica: 50, proatividade: 50, trabalhoEquipe: 50, pontualidade: 50 },
                conductStatus: selectedStaff.staffDetails?.conductStatus || 'green',
                level: selectedStaff.staffDetails?.level || 1,
                xp: selectedStaff.staffDetails?.xp || 0,
                infoCards: selectedStaff.staffDetails?.infoCards || [],
                conductCards: selectedStaff.staffDetails?.conductCards || [],
                conductHistory: selectedStaff.staffDetails?.conductHistory || []
            });
            setIsEditing(false);

            // Definir mês atual como padrão
            const currentMonth = new Date().toLocaleDateString('pt-BR', { month: 'long', year: 'numeric' });
            setSelectedFeedbackMonth(currentMonth.charAt(0).toUpperCase() + currentMonth.slice(1));
        }
    }, [selectedStaff]);

    // Helpers para Feedback
    const getCurrentMonthFeedback = () => {
        if (!editForm.monthlyFeedback) return '';
        const entry = editForm.monthlyFeedback.find(f => f.month === selectedFeedbackMonth);
        return entry ? entry.content : '';
    };

    const handleFeedbackChange = (newContent) => {
        setEditForm(prev => {
            const feedbacks = prev.monthlyFeedback || [];
            const index = feedbacks.findIndex(f => f.month === selectedFeedbackMonth);

            let newFeedbacks;
            if (index >= 0) {
                newFeedbacks = [...feedbacks];
                newFeedbacks[index] = { ...newFeedbacks[index], content: newContent, updatedAt: new Date().toISOString() };
            } else {
                newFeedbacks = [...feedbacks, { month: selectedFeedbackMonth, content: newContent, updatedAt: new Date().toISOString() }];
            }

            return {
                ...prev,
                monthlyFeedback: newFeedbacks
            };
        });
    };

    // Helpers para Ticket Stats
    const getMonthStats = () => {
        if (!editForm.ticketStats) return { cities: {} };
        const entry = editForm.ticketStats.find(s => s.month === selectedFeedbackMonth);
        return entry || {
            month: selectedFeedbackMonth,
            cities: {
                'Capital': { support: 0, coupon: 0, complaint: 0, other: 0 },
                'Morada do Valley': { support: 0, coupon: 0, complaint: 0, other: 0 },
                'Valley Norte': { support: 0, coupon: 0, complaint: 0, other: 0 },
                'Valley Sul': { support: 0, coupon: 0, complaint: 0, other: 0 }
            }
        };
    };

    const handleStatsChange = (city, field, value) => {
        setEditForm(prev => {
            const stats = prev.ticketStats || [];
            const index = stats.findIndex(s => s.month === selectedFeedbackMonth);

            let newStats;
            const currentMonthData = index >= 0 ? stats[index] : {
                month: selectedFeedbackMonth,
                cities: {
                    'Capital': { support: 0, coupon: 0, complaint: 0, other: 0 },
                    'Morada do Valley': { support: 0, coupon: 0, complaint: 0, other: 0 },
                    'Valley Norte': { support: 0, coupon: 0, complaint: 0, other: 0 },
                    'Valley Sul': { support: 0, coupon: 0, complaint: 0, other: 0 }
                }
            };

            const updatedCities = {
                ...currentMonthData.cities,
                [city]: {
                    ...(currentMonthData.cities?.[city] || { support: 0, coupon: 0, complaint: 0, other: 0 }),
                    [field]: value
                }
            };

            const updatedEntry = { ...currentMonthData, cities: updatedCities };

            if (index >= 0) {
                newStats = [...stats];
                newStats[index] = updatedEntry;
            } else {
                newStats = [...stats, updatedEntry];
            }

            return { ...prev, ticketStats: newStats };
        });
    };

    // Calculate Highlights
    const calculateHighlights = () => {
        const stats = editForm.ticketStats || [];
        if (stats.length === 0) return { bestMonth: 'N/A', worstMonth: 'N/A', mainCategory: 'N/A' };

        let maxTotal = -1;
        let minTotal = Infinity;
        let bestMonth = 'N/A';
        let worstMonth = 'N/A';

        const categoryTotals = { support: 0, coupon: 0, complaint: 0, other: 0 };

        stats.forEach(entry => {
            let monthTotal = 0;
            if (entry.cities) {
                Object.values(entry.cities).forEach(cityData => {
                    monthTotal += (cityData.support || 0) + (cityData.coupon || 0) + (cityData.complaint || 0) + (cityData.other || 0);
                    categoryTotals.support += (cityData.support || 0);
                    categoryTotals.coupon += (cityData.coupon || 0);
                    categoryTotals.complaint += (cityData.complaint || 0);
                    categoryTotals.other += (cityData.other || 0);
                });
            }

            if (monthTotal > maxTotal) {
                maxTotal = monthTotal;
                bestMonth = entry.month;
            }
            if (monthTotal < minTotal && monthTotal > 0) {
                minTotal = monthTotal;
                worstMonth = entry.month;
            }
        });

        if (minTotal === Infinity) worstMonth = 'N/A';

        // Find Main Category
        const mainCategory = Object.entries(categoryTotals).reduce((a, b) => a[1] > b[1] ? a : b)[0];
        const categoryLabels = { support: 'Suporte', coupon: 'Cupom', complaint: 'Denúncia', other: 'Outros' };

        return {
            bestMonth,
            worstMonth,
            mainCategory: categoryLabels[mainCategory] || 'N/A',
            maxTotal,
            minTotal: minTotal === Infinity ? 0 : minTotal
        };
    };

    const handleStatsNoteChange = (field, value) => {
        setEditForm(prev => {
            const stats = prev.ticketStats || [];
            const index = stats.findIndex(s => s.month === selectedFeedbackMonth);

            let newStats;
            const currentMonthData = index >= 0 ? stats[index] : {
                month: selectedFeedbackMonth,
                cities: {
                    'Capital': { support: 0, coupon: 0, complaint: 0, other: 0 },
                    'Morada do Valley': { support: 0, coupon: 0, complaint: 0, other: 0 },
                    'Valley Norte': { support: 0, coupon: 0, complaint: 0, other: 0 },
                    'Valley Sul': { support: 0, coupon: 0, complaint: 0, other: 0 }
                },
                notes: { title: '', description: '' }
            };

            const updatedNotes = {
                ...(currentMonthData.notes || { title: '', description: '' }),
                [field]: value
            };

            const updatedEntry = { ...currentMonthData, notes: updatedNotes };

            if (index >= 0) {
                newStats = [...stats];
                newStats[index] = updatedEntry;
            } else {
                newStats = [...stats, updatedEntry];
            }

            return { ...prev, ticketStats: newStats };
        });
    };

    const handleLaunchSave = async () => {
        const { city, support, coupon, complaint, other } = launchForm;

        // Calculate new stats locally first to get the object structure
        let updatedTicketStats = [...(editForm.ticketStats || [])];
        const index = updatedTicketStats.findIndex(s => s.month === selectedFeedbackMonth);

        let currentMonthData = index >= 0 ? updatedTicketStats[index] : {
            month: selectedFeedbackMonth,
            cities: {
                'Capital': { support: 0, coupon: 0, complaint: 0, other: 0 },
                'Morada do Valley': { support: 0, coupon: 0, complaint: 0, other: 0 },
                'Valley Norte': { support: 0, coupon: 0, complaint: 0, other: 0 },
                'Valley Sul': { support: 0, coupon: 0, complaint: 0, other: 0 }
            }
        };

        const updatedCities = {
            ...currentMonthData.cities,
            [city]: { support, coupon, complaint, other }
        };

        const updatedEntry = { ...currentMonthData, cities: updatedCities };

        if (index >= 0) {
            updatedTicketStats[index] = updatedEntry;
        } else {
            updatedTicketStats.push(updatedEntry);
        }

        // Update Local State
        setEditForm(prev => ({ ...prev, ticketStats: updatedTicketStats }));

        // Save to Backend
        try {
            const result = await adminFullUpdateUser(selectedStaff.discordId, {
                staffDetails: {
                    ...selectedStaff.staffDetails,
                    ticketStats: updatedTicketStats
                }
            });

            if (result.success) {
                toast.success(`Dados lançados para ${city} e salvos!`);
                setLaunchModalOpen(false);
                setLaunchForm(prev => ({ ...prev, support: 0, coupon: 0, complaint: 0, other: 0 }));

                // Update global user state (optional but good for consistency)
                setSelectedStaff(prev => ({
                    ...prev,
                    staffDetails: {
                        ...prev.staffDetails,
                        ticketStats: updatedTicketStats
                    }
                }));
            } else {
                toast.error("Erro ao salvar lançamento.");
            }
        } catch (error) {
            console.error("Erro ao salvar:", error);
            toast.error("Erro ao conectar com o servidor.");
        }
    };

    const handleSaveNotes = async () => {
        // Prepare data to save (from editForm which is already updated by handleStatsNoteChange)
        const updatedTicketStats = editForm.ticketStats || [];

        try {
            const result = await adminFullUpdateUser(selectedStaff.discordId, {
                staffDetails: {
                    ...selectedStaff.staffDetails,
                    ticketStats: updatedTicketStats
                }
            });

            if (result.success) {
                toast.success('Notas salvas com sucesso!');
                setIsEditingNotes(false);
                // Update global user state
                setSelectedStaff(prev => ({
                    ...prev,
                    staffDetails: {
                        ...prev.staffDetails,
                        ticketStats: updatedTicketStats
                    }
                }));
            } else {
                toast.error('Erro ao salvar notas.');
            }
        } catch (error) {
            console.error("Erro ao salvar notas:", error);
            toast.error("Erro ao salvar notas.");
        }
    };

    const handleSaveFicha = async () => {
        try {
            const result = await adminFullUpdateUser(selectedStaff.discordId, {
                staffDetails: {
                    ...selectedStaff.staffDetails,
                    mainFunction: editForm.mainFunction,
                    startDate: editForm.startDate,
                    shift: editForm.shift,
                    infoCards: editForm.infoCards
                }
            });

            if (result.success) {
                toast.success('Ficha atualizada!');
                setIsEditingFicha(false);
                setSelectedStaff(prev => ({
                    ...prev,
                    staffDetails: {
                        ...prev.staffDetails,
                        mainFunction: editForm.mainFunction,
                        startDate: editForm.startDate,
                        shift: editForm.shift,
                        infoCards: editForm.infoCards
                    }
                }));
            } else {
                toast.error('Erro ao salvar ficha.');
            }
        } catch (error) {
            console.error("Erro:", error);
            toast.error("Erro ao salvar ficha.");
        }
    };

    const highlights = calculateHighlights();

    // Helper functions for new features
    const handleSkillChange = (key, value) => {
        setEditForm(prev => ({
            ...prev,
            skills: {
                ...prev.skills,
                [key]: value
            }
        }));
    };

    const conductLabels = {
        green: 'Conduta Exemplar',
        blue: 'Novato / Treinamento',
        yellow: 'Atenção Necessária',
        orange: 'Advertência Verbal',
        red: 'Risco de Desligamento',
        purple: 'Staff Destaque'
    };

    const handleConductChange = (status) => {
        const oldStatus = editForm.conductStatus;
        if (oldStatus === status) return;

        setEditForm(prev => ({
            ...prev,
            conductStatus: status,
            conductHistory: [
                {
                    id: Date.now(),
                    date: new Date().toISOString(),
                    oldStatus,
                    newStatus: status,
                    title: 'Alteração de Conduta',
                    description: `Status alterado de "${conductLabels[oldStatus] || oldStatus}" para "${conductLabels[status] || status}"`
                },
                ...(prev.conductHistory || [])
            ]
        }));
    };

    // Generic Helper for Cards (Info & Conduct)
    const addCard = (type) => {
        const newCard = { id: Date.now(), title: '', description: '' };
        setEditForm(prev => ({
            ...prev,
            [type]: [...(prev[type] || []), newCard]
        }));
    };

    const removeCard = (type, id) => {
        setEditForm(prev => ({
            ...prev,
            [type]: prev[type].filter(c => c.id !== id)
        }));
    };

    const updateCard = (type, id, field, value) => {
        setEditForm(prev => ({
            ...prev,
            [type]: prev[type].map(c => c.id === id ? { ...c, [field]: value } : c)
        }));
    };

    const handleSave = async () => {
        if (!selectedStaff) return;

        const updatedDetails = {
            ...selectedStaff.staffDetails,
            ...editForm
        };

        const result = await adminFullUpdateUser(selectedStaff.discordId, {
            staffDetails: updatedDetails
        });

        if (result.success) {
            toast.success('Perfil atualizado com sucesso!');
            setIsEditing(false);
            // Update local selection to reflect changes immediately
            setSelectedStaff(prev => ({
                ...prev,
                staffDetails: updatedDetails
            }));
        } else {
            toast.error('Erro ao salvar: ' + result.error);
        }
    };

    const handleAddAlignment = async () => {
        if (!newAlignment.topic || !newAlignment.actions) return toast.error('Preencha tópico e ações.');

        const alignment = {
            ...newAlignment,
            date: new Date().toISOString(),
            id: Date.now()
        };

        const updatedAlignments = [alignment, ...(editForm.alignments || [])];
        const updatedDetails = {
            ...editForm,
            alignments: updatedAlignments
        };

        // If not in global edit mode, persist immediately
        if (!isEditing) {
            const result = await adminFullUpdateUser(selectedStaff.discordId, {
                staffDetails: updatedDetails
            });
            if (result.success) {
                setSelectedStaff(prev => ({ ...prev, staffDetails: updatedDetails }));
                setEditForm(updatedDetails);
                toast.success('Alinhamento salvo com sucesso!');
            } else {
                return toast.error('Erro ao salvar alinhamento: ' + result.error);
            }
        } else {
            setEditForm(updatedDetails);
            toast.success('Alinhamento adicionado (Clique em Salvar Alterações para persistir)');
        }

        setNewAlignment({ topic: '', actions: '', status: 'Pending' });
        setAlignmentsModalOpen(false);
    };

    const handleAddNote = async () => {
        if (!newNote.title || !newNote.description) return toast.error('Preencha título e descrição.');

        const note = {
            ...newNote,
            date: new Date().toISOString(),
            id: Date.now()
        };

        const updatedNotes = [note, ...(editForm.adminNotes || [])];
        const updatedDetails = {
            ...editForm,
            adminNotes: updatedNotes
        };

        // If not in global edit mode, persist immediately
        if (!isEditing) {
            const result = await adminFullUpdateUser(selectedStaff.discordId, {
                staffDetails: updatedDetails
            });
            if (result.success) {
                setSelectedStaff(prev => ({ ...prev, staffDetails: updatedDetails }));
                setEditForm(updatedDetails);
                toast.success('Nota salva com sucesso!');
            } else {
                return toast.error('Erro ao salvar nota: ' + result.error);
            }
        } else {
            setEditForm(updatedDetails);
            toast.success('Nota adicionada (Clique em Salvar Alterações para persistir)');
        }

        setNewNote({ title: '', description: '', category: 'Geral' });
        setNotesModalOpen(false);
    };

    const toggleCity = async (cityName) => {
        const currentCities = selectedStaff.cities || [];
        const cityExists = currentCities.some(c => c.city === cityName);

        let newCities;
        if (cityExists) {
            newCities = currentCities.filter(c => c.city !== cityName);
            toast.info(`Cidade ${cityName} removida do perfil.`);
        } else {
            // Remove any existing entry with the same city name (case-insensitive) to prevent duplicates
            const filteredCities = currentCities.filter(c => c.city.toLowerCase() !== cityName.toLowerCase());

            // Default setup for a new city
            newCities = [...filteredCities, {
                city: cityName,
                playerId: 'PENDENTE',
                characterName: selectedStaff.name,
                roles: [],
                status: 'verified' // Usually verified for manually added cities by admin
            }];
            toast.success(`Cidade ${cityName} adicionada ao perfil!`);
        }

        // Persist immediately as requested
        const result = await adminFullUpdateUser(selectedStaff.discordId, {
            cities: newCities
        });

        if (result.success) {
            setSelectedStaff(prev => ({ ...prev, cities: newCities }));
        } else {
            toast.error('Erro ao atualizar cidades: ' + result.error);
        }
    };

    const deleteAlignment = async (id) => {
        const updatedAlignments = editForm.alignments.filter(a => a.id !== id);
        const updatedDetails = {
            ...editForm,
            alignments: updatedAlignments
        };

        if (!isEditing) {
            const result = await adminFullUpdateUser(selectedStaff.discordId, {
                staffDetails: updatedDetails
            });
            if (result.success) {
                setSelectedStaff(prev => ({ ...prev, staffDetails: updatedDetails }));
                setEditForm(updatedDetails);
                toast.success('Alinhamento removido!');
            } else {
                toast.error('Erro ao remover: ' + result.error);
            }
        } else {
            setEditForm(updatedDetails);
            toast.success('Alinhamento removido da lista');
        }
    };

    const deleteNote = async (id) => {
        const updatedNotes = editForm.adminNotes.filter(n => n.id !== id);
        const updatedDetails = {
            ...editForm,
            adminNotes: updatedNotes
        };

        if (!isEditing) {
            const result = await adminFullUpdateUser(selectedStaff.discordId, {
                staffDetails: updatedDetails
            });
            if (result.success) {
                setSelectedStaff(prev => ({ ...prev, staffDetails: updatedDetails }));
                setEditForm(updatedDetails);
                toast.success('Nota removida!');
            } else {
                toast.error('Erro ao remover: ' + result.error);
            }
        } else {
            setEditForm(updatedDetails);
            toast.success('Nota removida da lista');
        }
    };

    const deleteConductHistory = async (id) => {
        const updatedHistory = editForm.conductHistory.filter(h => h.id !== id);
        const updatedDetails = {
            ...editForm,
            conductHistory: updatedHistory
        };

        if (!isEditing) {
            const result = await adminFullUpdateUser(selectedStaff.discordId, {
                staffDetails: updatedDetails
            });
            if (result.success) {
                setSelectedStaff(prev => ({ ...prev, staffDetails: updatedDetails }));
                setEditForm(updatedDetails);
                toast.success('Histórico removido!');
            } else {
                toast.error('Erro ao remover: ' + result.error);
            }
        } else {
            setEditForm(updatedDetails);
            toast.success('Entrada removida da lista');
        }
    };

    // Custom Cards Helpers
    const addCustomCard = () => {
        setEditForm(prev => ({
            ...prev,
            customCards: [...(prev.customCards || []), { id: Date.now(), title: '', description: '' }]
        }));
    };

    const removeCustomCard = (id) => {
        setEditForm(prev => ({
            ...prev,
            customCards: prev.customCards.filter(c => c.id !== id)
        }));
    };

    const updateCustomCard = (id, field, value) => {
        setEditForm(prev => ({
            ...prev,
            customCards: prev.customCards.map(c => c.id === id ? { ...c, [field]: value } : c)
        }));
    };

    // 1. Extrair Cidades Únicas dos Usuários e Ordenar
    const cities = useMemo(() => {
        const baseCities = ['Capital', 'Morada do Valley', 'Valley Norte', 'Valley Sul'];
        const citySet = new Set(baseCities);

        // Adiciona cidades que têm pelo menos um staff vinculado
        allUsers.forEach(user => {
            if (user.accessLevel && user.accessLevel !== 'user') {
                (user.cities || []).forEach(c => {
                    if (c.city && c.city !== 'N/A' && c.city !== 'GERAL') {
                        // Tenta encontrar uma correspondência insensible a maiúsculas/minúsculas nas cidades base
                        const match = baseCities.find(bc => bc.toLowerCase() === c.city.toLowerCase());
                        if (match) {
                            citySet.add(match);
                        } else {
                            citySet.add(c.city);
                        }
                    }
                });
            }
        });

        // Ordem específica solicitada + Geral no final
        const order = ['Capital', 'Morada do Valley', 'Valley Norte', 'Valley Sul', 'Geral'];
        const foundCities = Array.from(citySet);

        // Adicionar GERAL se não estiver na lista (simulado)
        if (!foundCities.includes('Geral') && !foundCities.includes('GERAL')) {
            foundCities.push('Geral');
        }

        return foundCities.sort((a, b) => {
            const indexA = order.indexOf(a);
            const indexB = order.indexOf(b);

            // Se ambos estão na lista de ordem, ordena pelo index
            if (indexA !== -1 && indexB !== -1) return indexA - indexB;
            // Se apenas A está na lista, A vem primeiro
            if (indexA !== -1) return -1;
            // Se apenas B está na lista, B vem primeiro
            if (indexB !== -1) return 1;
            // Se nenhum está, ordem alfabética
            return a.localeCompare(b);
        });
    }, [allUsers]);

    // Helper para cores das cidades
    const getCityStyle = (cityName) => {
        const styles = {
            'Capital': { gradient: 'from-blue-600 to-cyan-500', icon: Building2, shadow: 'shadow-blue-500/30' },
            'Morada do Valley': { gradient: 'from-purple-600 to-pink-500', icon: Building2, shadow: 'shadow-purple-500/30' },
            'Valley Norte': { gradient: 'from-emerald-600 to-green-500', icon: MapPin, shadow: 'shadow-emerald-500/30' },
            'Valley Sul': { gradient: 'from-orange-600 to-red-500', icon: MapPin, shadow: 'shadow-orange-500/30' },
            'Geral': { gradient: 'from-zinc-600 to-slate-500', icon: Users, shadow: 'shadow-zinc-500/30' },
            'Estatísticas': { gradient: 'from-purple-600 to-blue-600', icon: BarChart2, shadow: 'shadow-purple-500/30' }
        };
        return styles[cityName] || { gradient: 'from-blue-600/50 to-indigo-600/50', icon: Building2, shadow: 'shadow-blue-500/10' };
    };

    // 2. Filtrar Staffs pela Cidade Selecionada
    const filteredStaff = useMemo(() => {
        if (!selectedCity) return [];

        return allUsers.filter(user => {
            // Filtros básicos de Staff
            const isStaff = user.accessLevel && user.accessLevel !== 'user';
            if (!isStaff) return false;

            // Filtro de Texto
            if (searchTerm) {
                const term = searchTerm.toLowerCase();
                const matchesName = (user.name || '').toLowerCase().includes(term);
                const matchesCode = (user.staffDetails?.codename || '').toLowerCase().includes(term);
                const matchesDiscord = (String(user.discordId) || '').includes(term);
                if (!matchesName && !matchesCode && !matchesDiscord) return false;
            }

            // Filtro de Cidade
            if (selectedCity === 'Geral') {
                return true;
            }

            return user.cities?.some(c => c.city?.toLowerCase() === selectedCity.toLowerCase());
        });
    }, [allUsers, selectedCity, searchTerm]);

    // Render Helpers (moved before useMemo that uses them)
    const formatDate = (dateString) => {
        if (!dateString) return 'N/A';
        try {
            return format(new Date(dateString), 'dd/MM/yyyy HH:mm');
        } catch {
            return 'Data Inválida';
        }
    };

    const getRoleBadge = (level) => {
        return level ? level.replace(/_/g, ' ').toUpperCase() : 'STAFF';
    };

    const getStaffDisplayName = (staff) => {
        return staff.staffDetails?.codename
            || staff.staffDetails?.businessData?.codename
            || staff.cities?.find(c => c.characterName && c.characterName !== 'N/A' && c.characterName !== staff.name)?.characterName
            || staff.name
            || 'Staff';
    };

    // 3. Agregador de Estatísticas Globais (Equipe)
    const teamStats = useMemo(() => {
        const stats = {
            totalTickets: 0,
            categories: { support: 0, coupon: 0, complaint: 0, other: 0 },
            cityRanking: {},
            staffRanking: [],
            month: selectedFeedbackMonth || getMonthOptions()[0]
        };

        allUsers.forEach(user => {
            const isStaff = user.accessLevel && user.accessLevel !== 'user';
            if (!isStaff) return;

            const userStats = user.staffDetails?.ticketStats || [];
            const monthData = userStats.find(s => s.month === stats.month);

            if (monthData && monthData.cities) {
                let userTotalInMonth = 0;
                Object.entries(monthData.cities).forEach(([cityName, cityData]) => {
                    const cityTotal = (cityData.support || 0) + (cityData.coupon || 0) + (cityData.complaint || 0) + (cityData.other || 0);

                    // Global category totals
                    stats.categories.support += (cityData.support || 0);
                    stats.categories.coupon += (cityData.coupon || 0);
                    stats.categories.complaint += (cityData.complaint || 0);
                    stats.categories.other += (cityData.other || 0);
                    stats.totalTickets += cityTotal;
                    userTotalInMonth += cityTotal;

                    // City ranking
                    if (!stats.cityRanking[cityName]) {
                        stats.cityRanking[cityName] = { total: 0, support: 0, coupon: 0, complaint: 0, other: 0 };
                    }
                    stats.cityRanking[cityName].total += cityTotal;
                    stats.cityRanking[cityName].support += (cityData.support || 0);
                    stats.cityRanking[cityName].coupon += (cityData.coupon || 0);
                    stats.cityRanking[cityName].complaint += (cityData.complaint || 0);
                    stats.cityRanking[cityName].other += (cityData.other || 0);
                });

                // Staff ranking entry
                if (userTotalInMonth > 0) {
                    stats.staffRanking.push({
                        name: getStaffDisplayName(user),
                        discordId: user.discordId,
                        total: userTotalInMonth,
                        avatar: user.avatar
                    });
                }
            }
        });

        // Sort Rankings
        stats.staffRanking.sort((a, b) => b.total - a.total);
        const sortedCities = Object.entries(stats.cityRanking)
            .map(([name, data]) => ({ name, ...data }))
            .sort((a, b) => b.total - a.total);

        return { ...stats, cityRanking: sortedCities };
    }, [allUsers, selectedFeedbackMonth]);


    // --- VIEW 1: SELEÇÃO DE CIDADE ---
    if (!selectedCity) {
        return (
            <div className="space-y-8 animate-in fade-in duration-500 max-w-7xl mx-auto py-8">
                <div className="space-y-4 text-center">
                    <h1 className="text-4xl md:text-5xl font-black text-transparent bg-clip-text bg-gradient-to-r from-white via-blue-100 to-white uppercase tracking-tight">
                        Equipe Administrativa
                    </h1>
                    <p className="text-zinc-400 text-lg">Selecione uma base operacional para visualizar os membros</p>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 px-4">
                    {cities.map((city, idx) => {
                        const style = getCityStyle(city);
                        const CityIcon = style.icon;

                        return (
                            <motion.div
                                key={city}
                                initial={{ opacity: 0, y: 20 }}
                                animate={{ opacity: 1, y: 0 }}
                                transition={{ delay: idx * 0.1 }}
                            >
                                <Card
                                    onClick={() => setSelectedCity(city)}
                                    className={`bg-zinc-900/40 border-white/5 hover:border-white/20 cursor-pointer transition-all duration-500 group overflow-hidden relative h-56 ${style.shadow} hover:shadow-2xl hover:-translate-y-1`}
                                >
                                    {/* Gradient Overlay */}
                                    <div className={`absolute inset-0 bg-gradient-to-br ${style.gradient} opacity-0 group-hover:opacity-10 transition-opacity duration-500`} />

                                    {/* Blob Effect */}
                                    <div className={`absolute -right-20 -top-20 w-60 h-60 rounded-full bg-gradient-to-br ${style.gradient} opacity-[0.03] blur-3xl group-hover:opacity-[0.15] transition-all duration-700`} />
                                    <div className={`absolute -left-20 -bottom-20 w-60 h-60 rounded-full bg-gradient-to-tr ${style.gradient} opacity-[0.03] blur-3xl group-hover:opacity-[0.15] transition-all duration-700 delay-100`} />

                                    <CardContent className="h-full flex flex-col items-center justify-center relative z-10 gap-6">
                                        <div className={`w-20 h-20 rounded-2xl bg-gradient-to-br ${style.gradient} p-0.5 shadow-lg group-hover:scale-110 group-hover:shadow-[0_0_30px_rgba(255,255,255,0.2)] transition-all duration-500`}>
                                            <div className="w-full h-full bg-black/90 rounded-[14px] flex items-center justify-center backdrop-blur-sm">
                                                <CityIcon className="w-9 h-9 text-white drop-shadow-md" />
                                            </div>
                                        </div>

                                        <div className="text-center space-y-2">
                                            <h3 className="text-2xl font-black text-white group-hover:tracking-wide transition-all duration-300 drop-shadow-sm">{city}</h3>
                                            <div className="flex items-center justify-center gap-2">
                                                <div className="h-[2px] w-0 group-hover:w-12 bg-gradient-to-r from-transparent via-white to-transparent transition-all duration-700" />
                                                <p className="text-xs font-bold text-zinc-500 uppercase tracking-widest group-hover:text-white transition-colors delay-100">Ver Equipe</p>
                                                <div className="h-[2px] w-0 group-hover:w-12 bg-gradient-to-r from-transparent via-white to-transparent transition-all duration-700" />
                                            </div>
                                        </div>
                                    </CardContent>
                                </Card>
                            </motion.div>
                        );
                    })}
                </div>

                {/* ESTATÍSTICAS CARD (DESTAQUE) */}
                <div className="max-w-7xl mx-auto px-4 mt-8 pb-12">
                    <motion.div
                        initial={{ opacity: 0, scale: 0.95 }}
                        animate={{ opacity: 1, scale: 1 }}
                        transition={{ delay: 0.5 }}
                    >
                        <Card
                            onClick={() => setSelectedCity('TEAM_STATS')}
                            className="bg-zinc-900/40 border-white/5 hover:border-purple-500/30 cursor-pointer transition-all duration-500 group overflow-hidden relative"
                        >
                            <div className="absolute inset-0 bg-gradient-to-r from-purple-500/5 to-blue-500/5 opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
                            <CardContent className="py-8 flex flex-col md:flex-row items-center justify-between gap-6 relative z-10">
                                <div className="flex items-center gap-6">
                                    <div className="p-4 rounded-2xl bg-gradient-to-br from-purple-500 to-blue-500 shadow-xl group-hover:scale-110 transition-transform duration-500 shadow-purple-500/20">
                                        <BarChart2 className="w-8 h-8 text-white" />
                                    </div>
                                    <div>
                                        <h3 className="text-2xl font-black text-white italic tracking-tight">ESTATÍSTICAS DA EQUIPE</h3>
                                        <p className="text-zinc-400 text-sm font-medium">Ranking geral de cidades, produtividade e análise mensal</p>
                                    </div>
                                </div>
                                <div className="flex items-center gap-4">
                                    <div className="flex -space-x-3">
                                        {[1, 2, 3].map(i => (
                                            <div key={i} className="w-9 h-9 rounded-full border-2 border-zinc-900 bg-zinc-800 flex items-center justify-center">
                                                <Trophy className={`w-4 h-4 ${i === 1 ? 'text-yellow-500 shadow-[0_0_10px_rgba(234,179,8,0.3)]' : 'text-zinc-500'}`} />
                                            </div>
                                        ))}
                                    </div>
                                    <Button variant="ghost" className="text-white hover:bg-white/10 gap-2 font-bold uppercase text-[10px] tracking-widest px-8 h-12 border border-white/5 rounded-xl group-hover:border-purple-500/30 transition-all">
                                        Ver Dashboard <ExternalLink className="w-3 h-3 group-hover:translate-x-1 group-hover:-translate-y-1 transition-transform" />
                                    </Button>
                                </div>
                            </CardContent>
                        </Card>
                    </motion.div>
                </div>
            </div>
        );
    }

    // --- VIEW 2: DASHBOARD DE ESTATÍSTICAS DA EQUIPE ---
    if (selectedCity === 'TEAM_STATS') {
        return (
            <div className="min-h-screen bg-zinc-950 text-white pb-24">
                {/* Header fixo ou sticky se quiser, mas aqui vamos de simples */}
                <div className="bg-zinc-900/50 border-b border-white/5 backdrop-blur-md sticky top-0 z-50">
                    <div className="max-w-7xl mx-auto px-6 h-20 flex items-center justify-between">
                        <div className="flex items-center gap-4">
                            <Button
                                onClick={() => setSelectedCity(null)}
                                variant="ghost"
                                className="p-2 hover:bg-white/5 text-zinc-400 hover:text-white rounded-xl"
                            >
                                <ArrowLeft className="w-5 h-5" />
                            </Button>
                            <div>
                                <h1 className="text-xl font-bold italic tracking-tighter">TEAM DASHBOARD</h1>
                                <p className="text-[10px] text-zinc-500 uppercase font-black tracking-widest">Analytics & Performance</p>
                            </div>
                        </div>

                        <div className="flex items-center gap-4">
                            <Select value={selectedFeedbackMonth} onValueChange={setSelectedFeedbackMonth}>
                                <SelectTrigger className="w-48 bg-zinc-900 border-white/5 h-10 text-xs text-white rounded-xl focus:ring-0">
                                    <SelectValue />
                                </SelectTrigger>
                                <SelectContent className="bg-zinc-900 border-zinc-800 text-white">
                                    {getMonthOptions().map(m => (
                                        <SelectItem key={m} value={m}>{m}</SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                            <div className="h-10 w-10 rounded-xl bg-purple-500/10 border border-purple-500/20 flex items-center justify-center">
                                <TrendingUp className="w-4 h-4 text-purple-400" />
                            </div>
                        </div>
                    </div>
                </div>

                <div className="max-w-7xl mx-auto px-6 mt-12 space-y-12">
                    {/* 1. MÉTRICAS GLOBAIS (INSIDER) */}
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                        {[
                            { label: 'Total de Tickets', value: teamStats.totalTickets, icon: MessageSquare, color: 'from-blue-600 to-indigo-600', sub: 'Volume Global' },
                            { label: 'Melhor Staff', value: teamStats.staffRanking[0]?.name || 'N/A', icon: Trophy, color: 'from-yellow-500 to-amber-500', sub: `${teamStats.staffRanking[0]?.total || 0} Tickets` },
                            { label: 'Cidade Ativa', value: teamStats.cityRanking[0]?.name || 'N/A', icon: Building2, color: 'from-emerald-600 to-green-600', sub: `${teamStats.cityRanking[0]?.total || 0} Tickets` },
                            { label: 'Mês de Referência', value: teamStats.month, icon: Calendar, color: 'from-purple-600 to-pink-600', sub: 'Base de Dados' },
                        ].map((stat, i) => (
                            <motion.div
                                key={i}
                                initial={{ opacity: 0, y: 20 }}
                                animate={{ opacity: 1, y: 0 }}
                                transition={{ delay: i * 0.1 }}
                            >
                                <Card className="bg-zinc-900/40 border-white/5 overflow-hidden group hover:border-white/10 transition-all duration-500 relative">
                                    <div className={`absolute inset-0 bg-gradient-to-br ${stat.color} opacity-0 group-hover:opacity-5 transition-opacity duration-500`} />
                                    <CardContent className="p-6">
                                        <div className="flex justify-between items-start mb-4">
                                            <div className={`p-3 rounded-xl bg-gradient-to-br ${stat.color} shadow-lg group-hover:scale-110 transition-transform duration-500`}>
                                                <stat.icon className="w-5 h-5 text-white" />
                                            </div>
                                            <Badge variant="outline" className="text-[9px] uppercase font-bold border-white/5 text-zinc-500">Insider View</Badge>
                                        </div>
                                        <div className="space-y-1">
                                            <p className="text-[10px] uppercase font-black text-zinc-500 tracking-[0.1em]">{stat.label}</p>
                                            <h3 className="text-xl font-bold text-white truncate">{stat.value}</h3>
                                            <p className="text-[10px] font-medium text-zinc-400 italic">{stat.sub}</p>
                                        </div>
                                    </CardContent>
                                </Card>
                            </motion.div>
                        ))}
                    </div>

                    <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
                        {/* 2. RANKING DE STAFF (Lado Esquerdo) */}
                        <Card className="lg:col-span-4 bg-zinc-900/40 border-white/5 backdrop-blur-md overflow-hidden flex flex-col">
                            <CardHeader className="border-b border-white/[0.03] bg-white/[0.01]">
                                <div className="flex items-center gap-3">
                                    <div className="p-2 rounded-lg bg-yellow-500/10 border border-yellow-500/20">
                                        <Medal className="w-4 h-4 text-yellow-500" />
                                    </div>
                                    <div>
                                        <CardTitle className="text-sm font-bold text-white">Ranking de Produtividade</CardTitle>
                                        <p className="text-[9px] text-zinc-500 uppercase font-black tracking-widest">Top Performers da Staff</p>
                                    </div>
                                </div>
                            </CardHeader>
                            <CardContent className="p-0 flex-1">
                                <div className="divide-y divide-white/[0.03]">
                                    {teamStats.staffRanking.length > 0 ? teamStats.staffRanking.map((staff, idx) => (
                                        <div key={staff.discordId} className="p-4 flex items-center justify-between group hover:bg-white/[0.02] transition-colors">
                                            <div className="flex items-center gap-3">
                                                <div className={`w-6 h-6 rounded-full flex items-center justify-center text-[10px] font-black border ${idx === 0 ? 'bg-yellow-500/20 border-yellow-500/30 text-yellow-500' :
                                                    idx === 1 ? 'bg-zinc-400/20 border-zinc-400/30 text-zinc-400' :
                                                        idx === 2 ? 'bg-orange-900/20 border-orange-900/30 text-orange-400' :
                                                            'bg-black/40 border-white/5 text-zinc-500'
                                                    }`}>
                                                    {idx + 1}
                                                </div>
                                                <div className="h-8 w-8 rounded-full bg-zinc-800 border border-white/5 overflow-hidden">
                                                    {staff.avatar ? (
                                                        <img src={staff.avatar} alt="" className="w-full h-full object-cover" />
                                                    ) : (
                                                        <div className="w-full h-full flex items-center justify-center">
                                                            <UserPlus className="w-4 h-4 text-zinc-600" />
                                                        </div>
                                                    )}
                                                </div>
                                                <span className="text-sm font-bold text-zinc-200 group-hover:text-white transition-colors">{staff.name}</span>
                                            </div>
                                            <div className="text-right">
                                                <div className="text-sm font-black text-white italic">{staff.total}</div>
                                                <p className="text-[9px] text-zinc-500 uppercase font-bold tracking-tighter">Tickets</p>
                                            </div>
                                        </div>
                                    )) : (
                                        <div className="p-12 text-center text-zinc-600 italic text-xs">Nenhum dado produtivo este mês.</div>
                                    )}
                                </div>
                            </CardContent>
                        </Card>

                        {/* 3. DETALHAMENTO POR CIDADE E CATEGORIA (Lado Direito) */}
                        <div className="lg:col-span-8 space-y-8">
                            {/* CATEGORY DISTRIBUTION */}
                            <Card className="bg-zinc-900/40 border-white/5 overflow-hidden">
                                <CardHeader className="pb-4">
                                    <div className="flex items-center justify-between">
                                        <div className="flex items-center gap-3">
                                            <div className="p-2 rounded-lg bg-blue-500/10 border border-blue-500/20">
                                                <BarChart2 className="w-4 h-4 text-blue-500" />
                                            </div>
                                            <CardTitle className="text-sm font-bold text-white">Distribuição por Categoria</CardTitle>
                                        </div>
                                        <Badge variant="ghost" className="text-[10px] font-mono text-zinc-500">Global</Badge>
                                    </div>
                                </CardHeader>
                                <CardContent className="pt-2">
                                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                                        {[
                                            { label: 'Suporte', key: 'support', color: 'bg-blue-500', text: 'text-blue-400' },
                                            { label: 'Cupom', key: 'coupon', color: 'bg-emerald-500', text: 'text-emerald-400' },
                                            { label: 'Denúncia', key: 'complaint', color: 'bg-red-500', text: 'text-red-400' },
                                            { label: 'Outros', key: 'other', color: 'bg-zinc-500', text: 'text-zinc-400' },
                                        ].map(cat => {
                                            const val = teamStats.categories[cat.key];
                                            const pct = teamStats.totalTickets > 0 ? Math.round((val / teamStats.totalTickets) * 100) : 0;
                                            return (
                                                <div key={cat.key} className="bg-black/20 rounded-2xl p-4 border border-white/5 space-y-3">
                                                    <div className="flex justify-between items-center">
                                                        <span className="text-[10px] uppercase font-black text-zinc-500">{cat.label}</span>
                                                        <div className={`w-2 h-2 rounded-full ${cat.color}`} />
                                                    </div>
                                                    <div className="flex items-baseline gap-2">
                                                        <span className={`text-2xl font-black ${cat.text}`}>{val}</span>
                                                        <span className="text-[10px] font-bold text-zinc-600">{pct}%</span>
                                                    </div>
                                                    <div className="w-full h-1 bg-white/5 rounded-full overflow-hidden">
                                                        <motion.div
                                                            initial={{ width: 0 }}
                                                            animate={{ width: `${pct}%` }}
                                                            className={`h-full ${cat.color}`}
                                                        />
                                                    </div>
                                                </div>
                                            );
                                        })}
                                    </div>
                                </CardContent>
                            </Card>

                            {/* CITY BREAKDOWN */}
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                {teamStats.cityRanking.map((city, idx) => {
                                    const style = getCityStyle(city.name);
                                    return (
                                        <Card key={city.name} className="bg-zinc-900/40 border-white/5 overflow-hidden group">
                                            <div className={`h-1 w-full bg-gradient-to-r ${style.gradient} opacity-50`} />
                                            <CardContent className="p-5">
                                                <div className="flex justify-between items-center mb-6">
                                                    <div className="flex items-center gap-3">
                                                        <div className={`p-2 rounded-lg bg-gradient-to-br ${style.gradient} shadow-lg opacity-80`}>
                                                            <Building2 className="w-4 h-4 text-white" />
                                                        </div>
                                                        <h4 className="font-bold text-white tracking-tight">{city.name}</h4>
                                                    </div>
                                                    <div className="text-right">
                                                        <div className="text-lg font-black text-white italic">{city.total}</div>
                                                        <p className="text-[9px] text-zinc-500 uppercase font-bold tracking-tighter">Total Tickets</p>
                                                    </div>
                                                </div>

                                                <div className="space-y-4">
                                                    {[
                                                        { label: 'Suporte', val: city.support, color: 'bg-blue-400' },
                                                        { label: 'Cupom', val: city.coupon, color: 'bg-emerald-400' },
                                                        { label: 'Denúncia', val: city.complaint, color: 'bg-red-400' },
                                                        { label: 'Outros', val: city.other, color: 'bg-zinc-500' }
                                                    ].map(item => (
                                                        <div key={item.label} className="space-y-1.5">
                                                            <div className="flex justify-between text-[9px] uppercase font-bold text-zinc-500">
                                                                <span>{item.label}</span>
                                                                <span className="text-zinc-300">{item.val}</span>
                                                            </div>
                                                            <div className="h-1 w-full bg-white/5 rounded-full overflow-hidden">
                                                                <motion.div
                                                                    initial={{ width: 0 }}
                                                                    animate={{ width: `${city.total > 0 ? (item.val / city.total) * 100 : 0}%` }}
                                                                    className={`h-full ${item.color} opacity-60`}
                                                                />
                                                            </div>
                                                        </div>
                                                    ))}
                                                </div>
                                            </CardContent>
                                        </Card>
                                    );
                                })}
                            </div>
                        </div>
                    </div>

                    {/* 4. FOOTER INFO */}
                    <div className="pt-12 border-t border-white/5 flex flex-col items-center gap-4">
                        <div className="flex items-center gap-2 p-3 bg-zinc-900/50 rounded-2xl border border-white/5">
                            <History className="w-4 h-4 text-zinc-500" />
                            <p className="text-xs text-zinc-500">
                                Dados agregados com base nos lançamentos manuais realizados na aba **Review** da equipe em **{teamStats.month}**.
                            </p>
                        </div>
                        <Badge variant="outline" className="text-[8px] border-white/5 text-zinc-600 uppercase tracking-[0.3em]">Sistema de Auditoria Global v2.0</Badge>
                    </div>
                </div>
            </div>
        );
    }

    // --- VIEW 3: PERFIL DETALHADO DO STAFF (SOCIAL NETWORK STYLE) ---
    if (selectedStaff) {
        const codename = getStaffDisplayName(selectedStaff);
        const cityStyle = getCityStyle(selectedStaff.cities?.[0]?.city || 'Geral');

        // Calculate Level (Mock logic based on tenure/stats)
        const calculateLevel = () => {
            const stats = selectedStaff.staffDetails?.ticketStats || [];
            const totalTickets = stats.reduce((acc, curr) => acc + (curr.support || 0) + (curr.coupon || 0), 0);
            return Math.floor(totalTickets / 100) + 1;
        };

        const level = calculateLevel();
        const xpProgress = 65; // Mock for visual

        return (
            <div className="min-h-screen -mx-4 -mt-4 relative overflow-hidden font-sans selection:bg-purple-500/30 animate-in fade-in duration-500">
                {/* 1. HERO HEADER (Social Style) */}
                <div className="relative h-80 group">
                    <div className={`absolute inset-0 bg-gradient-to-r ${cityStyle.gradient} opacity-20`}></div>
                    <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top,_var(--tw-gradient-stops))] from-black/0 via-[#09090b]/40 to-[#09090b]"></div>

                    {/* Animated Noise Texture */}
                    <div className="absolute inset-0 opacity-[0.03] bg-[url('https://grainy-gradients.vercel.app/noise.svg')] mix-blend-overlay"></div>

                    {/* Top Bar Actions - REMOVED (Moved to Right Column) */}
                    <div className="absolute top-6 left-6 right-6 flex justify-between items-center z-20">
                        <Button
                            onClick={() => setSelectedStaff(null)}
                            variant="ghost"
                            className="text-zinc-400 hover:text-white hover:bg-white/5 gap-2 rounded-full"
                        >
                            <ArrowLeft className="w-5 h-5" /> Voltar
                        </Button>
                    </div>

                    {/* Profile Identity Block */}
                    <div className="absolute -bottom-16 left-0 right-0 max-w-7xl mx-auto px-6 flex items-end gap-8 z-10">
                        {/* Avatar Hub */}
                        <div className="relative group">
                            <div className="w-40 h-40 rounded-3xl bg-[#09090b] p-2 overflow-hidden ring-1 ring-white/10 shadow-2xl relative z-10">
                                <div className="w-full h-full rounded-2xl overflow-hidden relative bg-zinc-900 group-hover:scale-105 transition-transform duration-500">
                                    {selectedStaff.avatar ? (
                                        <img src={`https://cdn.discordapp.com/avatars/${selectedStaff.discordId}/${selectedStaff.avatar}.png`} className="w-full h-full object-cover" />
                                    ) : (
                                        <div className="flex items-center justify-center w-full h-full"><Users className="w-12 h-12 text-zinc-700" /></div>
                                    )}
                                </div>
                            </div>
                            {/* Role Badge */}
                            <div className="absolute -bottom-3 -right-3 z-20 bg-gradient-to-r from-purple-600 to-pink-600 text-white text-[10px] uppercase font-bold px-3 py-1 rounded-full shadow-lg border border-purple-400/30">
                                {getRoleBadge(selectedStaff.accessLevel)}
                            </div>
                        </div>

                        {/* Name & Quick Stats */}
                        <div className="flex-1 pb-4">
                            <h1 className="text-4xl font-black text-white tracking-tight mb-2 flex items-center gap-3 drop-shadow-lg">
                                {codename}
                                <Badge className="bg-white/10 hover:bg-white/20 text-white border-0 backdrop-blur-md">LVL {level}</Badge>
                            </h1>
                            <div className="flex items-center gap-6 text-zinc-400 text-sm font-medium">
                                <span className="flex items-center gap-2"><MapPin className="w-4 h-4 text-purple-500" /> {selectedStaff.cities?.map(c => c.city).join(', ') || 'Sem Cidade'}</span>
                                <span className="flex items-center gap-2"><Clock className="w-4 h-4 text-emerald-500" /> {selectedStaff.staffDetails?.startDate ? format(new Date(selectedStaff.staffDetails.startDate), 'MMMM yyyy') : 'Data N/A'}</span>
                            </div>
                        </div>

                        {/* Global Edit/Save Actions in Header */}
                        <div className="flex items-center gap-3">
                            {isEditing ? (
                                <>
                                    <Button onClick={handleSave} className="bg-emerald-500 hover:bg-emerald-600 text-white shadow-[0_0_20px_rgba(16,185,129,0.3)] border-0 font-bold rounded-xl h-11 px-6">
                                        <Save className="w-4 h-4 mr-2" /> Salvar Alterações
                                    </Button>
                                    <Button onClick={() => setIsEditing(false)} variant="outline" className="border-zinc-700 bg-zinc-900/50 hover:bg-zinc-800 text-zinc-400 hover:text-white rounded-xl h-11">
                                        Cancelar
                                    </Button>
                                </>
                            ) : (
                                <Button onClick={() => setIsEditing(true)} className="bg-white/5 hover:bg-white/10 text-white border border-white/10 backdrop-blur-md rounded-xl h-11 font-bold group px-6">
                                    <Eye className="w-4 h-4 mr-2 group-hover:text-purple-400 transition-colors" /> Modo Edição
                                </Button>
                            )}
                        </div>
                    </div>
                </div>

                {/* 2. TABS & MAIN CONTENT */}
                <div className="max-w-7xl mx-auto px-6 mt-24 pb-24">
                    <Tabs defaultValue="overview" className="w-full space-y-8">
                        <TabsList className="bg-zinc-900/50 border border-white/5 p-1 rounded-full backdrop-blur-md">
                            <TabsTrigger value="overview" className="rounded-full data-[state=active]:bg-purple-600 data-[state=active]:text-white text-zinc-400">Visão Geral</TabsTrigger>
                            <TabsTrigger value="timeline" className="rounded-full data-[state=active]:bg-blue-600 data-[state=active]:text-white text-zinc-400">Timeline</TabsTrigger>
                            <TabsTrigger value="conduct" className="rounded-full data-[state=active]:bg-amber-600 data-[state=active]:text-white text-zinc-400">Conduta</TabsTrigger>
                            <TabsTrigger value="info" className="rounded-full data-[state=active]:bg-emerald-600 data-[state=active]:text-white text-zinc-400">Informações</TabsTrigger>
                            <TabsTrigger value="stats" className="rounded-full data-[state=active]:bg-pink-600 data-[state=active]:text-white text-zinc-400">Estatísticas</TabsTrigger>
                        </TabsList>

                        <TabsContent value="overview" className="outline-none animate-in fade-in slide-in-from-bottom-4 duration-500">
                            <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">

                                {/* 1. INSIDER CARD: IDENTITY & TEAM */}
                                <Card className="lg:col-span-4 bg-zinc-900/40 border-white/5 backdrop-blur-md overflow-hidden relative group">
                                    <div className="absolute top-0 left-0 w-full h-[1px] bg-gradient-to-r from-transparent via-purple-500/40 to-transparent" />
                                    <CardHeader className="pb-2 border-b border-white/[0.03] bg-white/[0.01]">
                                        <div className="flex items-center gap-3">
                                            <div className="p-2 rounded-xl bg-purple-500/10 border border-purple-500/20">
                                                <Users className="w-4 h-4 text-purple-400" />
                                            </div>
                                            <div>
                                                <CardTitle className="text-sm font-bold text-white tracking-tight">Insider: Identidade & Equipe</CardTitle>
                                                <p className="text-[10px] text-zinc-500 uppercase font-black tracking-widest">Resumo do Perfil</p>
                                            </div>
                                        </div>
                                    </CardHeader>
                                    <CardContent className="pt-6 space-y-6">
                                        <div className="grid grid-cols-2 gap-4">
                                            <div className="space-y-1">
                                                <label className="text-[10px] uppercase font-bold text-zinc-600">Função Atual</label>
                                                <p className="text-sm font-bold text-zinc-200">{editForm.mainFunction || 'Não definido'}</p>
                                            </div>
                                            <div className="space-y-1">
                                                <label className="text-[10px] uppercase font-bold text-zinc-600">Turno Alocado</label>
                                                <p className="text-sm font-bold text-zinc-200">{editForm.shift || 'Flexible'}</p>
                                            </div>
                                            <div className="space-y-1">
                                                <label className="text-[10px] uppercase font-bold text-zinc-600">Nível Senioridade</label>
                                                <p className="text-sm font-bold text-purple-400">Level {calculateLevel()}</p>
                                            </div>
                                            <div className="space-y-1">
                                                <label className="text-[10px] uppercase font-bold text-zinc-600">Tempo de Atuação</label>
                                                <p className="text-sm font-bold text-zinc-200">
                                                    {selectedStaff.staffDetails?.startDate ?
                                                        format(new Date(selectedStaff.staffDetails.startDate), 'MMM yyyy', { locale: ptBR }) : 'N/A'}
                                                </p>
                                            </div>
                                        </div>

                                        <div className="space-y-3 pt-2">
                                            <label className="text-[10px] uppercase font-bold text-zinc-600">Cidades Vinculadas</label>
                                            <div className="flex flex-wrap gap-2">
                                                {selectedStaff.cities?.map(c => (
                                                    <Badge key={c.city} variant="outline" className="bg-black/40 border-white/10 text-zinc-300 text-[10px] py-1 px-3 rounded-lg">
                                                        <MapPin className="w-3 h-3 mr-1.5 text-purple-500" /> {c.city}
                                                    </Badge>
                                                )) || <span className="text-xs text-zinc-500">Nenhuma cidade vinculada</span>}
                                            </div>
                                        </div>
                                    </CardContent>
                                </Card>

                                {/* 2. INSIDER CARD: PERFORMANCE HISTÓRICA */}
                                <Card className="lg:col-span-4 bg-zinc-900/40 border-white/5 backdrop-blur-md overflow-hidden relative group">
                                    <div className="absolute top-0 left-0 w-full h-[1px] bg-gradient-to-r from-transparent via-blue-500/40 to-transparent" />
                                    <CardHeader className="pb-2 border-b border-white/[0.03] bg-white/[0.01]">
                                        <div className="flex items-center gap-3">
                                            <div className="p-2 rounded-xl bg-blue-500/10 border border-blue-500/20">
                                                <Activity className="w-4 h-4 text-blue-400" />
                                            </div>
                                            <div>
                                                <CardTitle className="text-sm font-bold text-white tracking-tight">Insider: Performance Histórica</CardTitle>
                                                <p className="text-[10px] text-zinc-500 uppercase font-black tracking-widest">Métrica de Tickets</p>
                                            </div>
                                        </div>
                                    </CardHeader>
                                    <CardContent className="pt-6">
                                        <div className="grid grid-cols-2 gap-3 mb-6">
                                            {(() => {
                                                const stats = getMonthStats();
                                                const metrics = [
                                                    { label: 'Suporte', key: 'support', color: 'text-blue-400', bg: 'bg-blue-400/5' },
                                                    { label: 'Cupom', key: 'coupon', color: 'text-emerald-400', bg: 'bg-emerald-400/5' },
                                                    { label: 'Denúncia', key: 'complaint', color: 'text-red-400', bg: 'bg-red-400/5' },
                                                    { label: 'Outros', key: 'other', color: 'text-zinc-400', bg: 'bg-zinc-400/5' }
                                                ];

                                                return metrics.map(m => {
                                                    // Aggregate value across all cities for the current month
                                                    const val = Object.values(stats.cities || {}).reduce((acc, cityData) => acc + (cityData[m.key] || 0), 0);
                                                    return (
                                                        <div key={m.key} className={`${m.bg} rounded-xl p-3 border border-white/[0.03] group/item hover:border-white/10 transition-colors`}>
                                                            <label className="text-[9px] uppercase font-bold text-zinc-500 block mb-1">{m.label}</label>
                                                            <div className={`text-xl font-black ${m.color}`}>{val}</div>
                                                        </div>
                                                    );
                                                });
                                            })()}
                                        </div>

                                        <div className="bg-black/20 rounded-xl p-4 border border-white/5 flex items-center justify-between">
                                            <div>
                                                <div className="text-[10px] font-black text-zinc-500 uppercase mb-1">Total Acumulado</div>
                                                <div className="text-2xl font-black text-white italic">
                                                    {(() => {
                                                        const stats = getMonthStats();
                                                        return Object.values(stats.cities || {}).reduce((acc, cityData) =>
                                                            acc + (cityData.support || 0) + (cityData.coupon || 0) + (cityData.complaint || 0) + (cityData.other || 0), 0
                                                        );
                                                    })()} <span className="text-[10px] not-italic text-zinc-500">Tickets</span>
                                                </div>
                                            </div>
                                            <div className="h-10 w-10 rounded-full border border-white/5 flex items-center justify-center bg-zinc-900">
                                                <TrendingUp className="w-5 h-5 text-emerald-500" />
                                            </div>
                                        </div>
                                    </CardContent>
                                </Card>

                                {/* 3. INSIDER CARD: CONDUTA & ATIVIDADE */}
                                <Card className="lg:col-span-4 bg-zinc-900/40 border-white/5 backdrop-blur-md overflow-hidden relative group">
                                    <div className="absolute top-0 left-0 w-full h-[1px] bg-gradient-to-r from-transparent via-amber-500/40 to-transparent" />
                                    <CardHeader className="pb-2 border-b border-white/[0.03] bg-white/[0.01]">
                                        <div className="flex items-center gap-3">
                                            <div className="p-2 rounded-xl bg-amber-500/10 border border-amber-500/20">
                                                <AlertTriangle className="w-4 h-4 text-amber-400" />
                                            </div>
                                            <div>
                                                <CardTitle className="text-sm font-bold text-white tracking-tight">Insider: Conduta & Feed</CardTitle>
                                                <p className="text-[10px] text-zinc-500 uppercase font-black tracking-widest">Status de Conformidade</p>
                                            </div>
                                        </div>
                                    </CardHeader>
                                    <CardContent className="pt-6 space-y-6">
                                        <div className="flex items-center justify-between bg-black/40 rounded-2xl p-4 border border-white/5">
                                            <div>
                                                <label className="text-[10px] uppercase font-bold text-zinc-500 block mb-1">Status Atual</label>
                                                <div className="flex items-center gap-2">
                                                    <div className={`w-3 h-3 rounded-full ${editForm.conductStatus === 'green' ? 'bg-emerald-500 shadow-[0_0_10px_rgba(16,185,129,0.5)]' :
                                                        editForm.conductStatus === 'purple' ? 'bg-purple-500 shadow-[0_0_10px_rgba(168,85,247,0.5)]' :
                                                            editForm.conductStatus === 'red' ? 'bg-red-500 shadow-[0_0_10px_rgba(239,68,68,0.5)]' :
                                                                'bg-amber-500 shadow-[0_0_10px_rgba(245,158,11,0.5)]'
                                                        }`} />
                                                    <span className="text-sm font-bold text-white italic uppercase tracking-tighter">
                                                        {conductLabels[editForm.conductStatus] || editForm.conductStatus}
                                                    </span>
                                                </div>
                                            </div>
                                            <Button variant="outline" size="sm" onClick={() => setActiveTab('conduct')} className="h-8 text-[10px] bg-zinc-900 border-white/5 text-zinc-400 hover:text-white rounded-lg">
                                                Ver Histórico
                                            </Button>
                                        </div>

                                        <div className="space-y-4">
                                            <div className="flex items-center justify-between border-b border-white/5 pb-2">
                                                <label className="text-[10px] uppercase font-bold text-zinc-500">Última Atividade</label>
                                                <Badge variant="ghost" className="text-[9px] font-mono text-zinc-600">Sync Live</Badge>
                                            </div>
                                            {(() => {
                                                const lastAtv = editForm.adminNotes?.[0] || editForm.alignments?.[0] || editForm.conductHistory?.[0];
                                                if (!lastAtv) return <p className="text-xs text-zinc-600 italic">Nenhuma atividade recente registrada.</p>;
                                                return (
                                                    <div className="flex gap-3">
                                                        <div className="p-2 rounded-lg bg-zinc-900 border border-white/5 h-fit">
                                                            <Clock className="w-3 h-3 text-zinc-500" />
                                                        </div>
                                                        <div className="flex-1 min-w-0">
                                                            <div className="text-[11px] font-bold text-zinc-200 truncate">{lastAtv.title || lastAtv.topic}</div>
                                                            <p className="text-[10px] text-zinc-500 line-clamp-2 mt-1">{lastAtv.description || lastAtv.actions}</p>
                                                        </div>
                                                    </div>
                                                );
                                            })()}
                                        </div>
                                    </CardContent>
                                </Card>

                                {/* 4. SOFT SKILLS: PREMIUM RADAR */}
                                <Card className="lg:col-span-12 bg-zinc-900/40 border-white/5 backdrop-blur-md overflow-hidden relative mt-4">
                                    <div className="absolute top-0 right-0 w-64 h-64 bg-purple-500/5 blur-[100px] -z-10" />
                                    <div className="absolute bottom-0 left-0 w-64 h-64 bg-blue-500/5 blur-[100px] -z-10" />
                                    <CardHeader className="pb-0 border-b border-white/[0.03] bg-white/[0.01]">
                                        <div className="flex items-center gap-3 py-2">
                                            <div className="p-2 rounded-xl bg-pink-500/10 border border-pink-500/20">
                                                <Target className="w-4 h-4 text-pink-400" />
                                            </div>
                                            <div>
                                                <CardTitle className="text-sm font-bold text-white tracking-tight">Soft Skills: Avaliação de Perfil</CardTitle>
                                                <p className="text-[10px] text-zinc-500 uppercase font-black tracking-widest">Radar de Competências</p>
                                            </div>
                                        </div>
                                    </CardHeader>
                                    <CardContent className="flex flex-col md:flex-row items-center justify-between gap-12 py-12 px-12">
                                        <div className="flex-1 space-y-6 max-w-md order-2 md:order-1">
                                            <div className="space-y-2">
                                                <h3 className="text-2xl font-black text-white italic uppercase tracking-tighter">Performance Comportamental</h3>
                                                <p className="text-sm text-zinc-400 leading-relaxed">
                                                    Este gráfico representa o equilíbrio entre habilidades técnicas, comunicação e proatividade.
                                                    Valores acima de 80 indicam excelência no pilar específico.
                                                </p>
                                            </div>

                                            <div className="grid grid-cols-2 gap-6 pt-4">
                                                {Object.entries(editForm.skills || {}).map(([key, val]) => (
                                                    <div key={key} className="space-y-2">
                                                        <div className="flex justify-between items-end">
                                                            <span className="text-[10px] uppercase font-bold text-zinc-500">{key.replace(/([A-Z])/g, ' $1')}</span>
                                                            <span className={`text-sm font-black ${val > 80 ? 'text-emerald-400' : 'text-zinc-300'}`}>{val}%</span>
                                                        </div>
                                                        <div className="h-1.5 w-full bg-black/40 rounded-full overflow-hidden border border-white/5 p-[1px]">
                                                            <motion.div
                                                                initial={{ width: 0 }}
                                                                animate={{ width: `${val}%` }}
                                                                transition={{ duration: 1, ease: "easeOut" }}
                                                                className={`h-full rounded-full ${val > 80 ? 'bg-gradient-to-r from-emerald-600 to-emerald-400 shadow-[0_0_8px_rgba(52,211,153,0.3)]' :
                                                                    val > 50 ? 'bg-gradient-to-r from-purple-600 to-purple-400' :
                                                                        'bg-zinc-700'
                                                                    }`}
                                                            />
                                                        </div>
                                                    </div>
                                                ))}
                                            </div>
                                        </div>

                                        <div className="relative order-1 md:order-2 flex justify-center items-center bg-black/20 rounded-full p-4 border border-white/5 shadow-2xl backdrop-blur-sm">
                                            <div className="absolute inset-0 bg-purple-500/10 rounded-full blur-3xl opacity-50" />
                                            <SkillRadar
                                                skills={editForm.skills}
                                                readOnly={!isEditing}
                                                onChange={(skill, val) => setEditForm(prev => ({
                                                    ...prev,
                                                    skills: { ...prev.skills, [skill]: val }
                                                }))}
                                            />
                                        </div>
                                    </CardContent>
                                </Card>

                                {/* 5. TIMELINE PREVIEW BLOCK */}
                                <div className="lg:col-span-12 mt-6">
                                    <Card className="bg-[#0c0c0e] border-white/5 relative overflow-hidden">
                                        <CardHeader className="bg-white/[0.01] border-b border-white/[0.03]">
                                            <div className="flex items-center justify-between">
                                                <CardTitle className="text-lg font-bold text-white flex items-center gap-2">
                                                    <div className="w-2 h-2 rounded-full bg-purple-500 animate-pulse shadow-[0_0_8px_rgba(168,85,247,0.8)]" />
                                                    Fluxo de Atividades Recentes
                                                </CardTitle>
                                                <Button variant="ghost" size="sm" onClick={() => {
                                                    document.querySelector('[data-value="timeline"]')?.click();
                                                }} className="text-xs text-zinc-500 hover:text-white">Ver Timeline Completa</Button>
                                            </div>
                                        </CardHeader>
                                        <CardContent className="pt-8">
                                            <TimelineFeed
                                                staff={{ ...selectedStaff, staffDetails: editForm }}
                                                isEditing={isEditing}
                                                onDeleteNote={deleteNote}
                                                onDeleteAlignment={deleteAlignment}
                                                onDeleteConduct={deleteConductHistory}
                                            />
                                        </CardContent>
                                    </Card>
                                </div>
                            </div>
                        </TabsContent>

                        {/* Placeholder Content for other tabs (Reusing existing components for now) */}
                        <TabsContent value="timeline" className="animate-in fade-in slide-in-from-bottom-4 duration-500 space-y-6">
                            {/* Quick Actions Bar (Moved from Overview) */}
                            <div className="flex items-center gap-2 overflow-x-auto pb-2 scrollbar-hide">
                                <Button onClick={() => setNotesModalOpen(true)} size="sm" variant="outline" className="border-dashed border-white/10 hover:border-purple-500 hover:text-purple-400 bg-zinc-900/50 text-zinc-400 rounded-xl px-4">
                                    <Plus className="w-4 h-4 mr-2" /> Nova Nota
                                </Button>
                                <Button onClick={() => setAlignmentsModalOpen(true)} size="sm" variant="outline" className="border-dashed border-white/10 hover:border-amber-500 hover:text-amber-400 bg-zinc-900/50 text-zinc-400 rounded-xl px-4">
                                    <Plus className="w-4 h-4 mr-2" /> Novo Alinhamento
                                </Button>
                            </div>

                            <Card className="bg-[#0c0c0e] border-white/5">
                                <CardHeader className="flex flex-row items-center justify-between">
                                    <CardTitle>Histórico Completo</CardTitle>
                                    <div className="text-[10px] text-zinc-500 uppercase font-bold tracking-widest flex items-center gap-2">
                                        <RefreshCw className="w-3 h-3 animate-spin-slow" /> Atualizado em Tempo Real
                                    </div>
                                </CardHeader>
                                <CardContent>
                                    <TimelineFeed
                                        staff={{ ...selectedStaff, staffDetails: editForm }}
                                        isEditing={isEditing}
                                        onDeleteNote={deleteNote}
                                        onDeleteAlignment={deleteAlignment}
                                        onDeleteConduct={deleteConductHistory}
                                    />
                                </CardContent>
                            </Card>
                        </TabsContent>

                        <TabsContent value="conduct" className="animate-in fade-in slide-in-from-bottom-4 duration-500">
                            <div className="max-w-4xl mx-auto space-y-6">
                                <ConductStatus status={editForm.conductStatus} history={[]} />
                                {isEditing && (
                                    <div className="flex flex-wrap gap-2 justify-center p-3 bg-zinc-900/80 backdrop-blur-md rounded-2xl border border-white/5 shadow-xl max-w-sm mx-auto">
                                        <div className="w-full text-[10px] uppercase font-bold text-zinc-500 tracking-widest text-center mb-2">Alterar Status</div>
                                        {[
                                            { id: 'purple', color: '#a855f7' },
                                            { id: 'blue', color: '#3b82f6' },
                                            { id: 'green', color: '#10b981' },
                                            { id: 'yellow', color: '#f59e0b' },
                                            { id: 'orange', color: '#f97316' },
                                            { id: 'red', color: '#ef4444' }
                                        ].map(s => (
                                            <button
                                                key={s.id}
                                                onClick={() => handleConductChange(s.id)}
                                                className={`w-8 h-8 rounded-full transition-all hover:scale-110 ${s.id === editForm.conductStatus ? 'ring-2 ring-white scale-110 shadow-lg shadow-white/20' : 'opacity-40 hover:opacity-100'}`}
                                                style={{ backgroundColor: s.color }}
                                                title={s.id}
                                            />
                                        ))}
                                    </div>
                                )}

                                {/* Conduct Cards */}
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                    {editForm.conductCards?.map(card => (
                                        <Card key={card.id} className="bg-zinc-900/50 border-white/5 relative group">
                                            <CardHeader className="pb-2">
                                                <div className="flex justify-between items-start">
                                                    {isEditing ? (
                                                        <Input value={card.title} onChange={e => updateCard('conductCards', card.id, 'title', e.target.value)} className="h-8 font-bold bg-transparent border-none p-0 text-white placeholder:text-zinc-500 focus-visible:ring-0" placeholder="Título" />
                                                    ) : (
                                                        <CardTitle className="text-sm font-bold text-white">{card.title}</CardTitle>
                                                    )}
                                                    {isEditing && <Trash2 onClick={() => removeCard('conductCards', card.id)} className="w-4 h-4 text-red-500 cursor-pointer opacity-0 group-hover:opacity-100 transition-opacity" />}
                                                </div>
                                            </CardHeader>
                                            <CardContent>
                                                {isEditing ? (
                                                    <Textarea value={card.description} onChange={e => updateCard('conductCards', card.id, 'description', e.target.value)} className="min-h-[80px] text-xs bg-zinc-950/50 border-white/10 text-zinc-300" placeholder="Descrição..." />
                                                ) : (
                                                    <p className="text-zinc-400 text-xs leading-relaxed">{card.description}</p>
                                                )}
                                            </CardContent>
                                        </Card>
                                    ))}
                                    {isEditing && (
                                        <Button onClick={() => addCard('conductCards')} variant="outline" className="h-full min-h-[150px] border-dashed border-white/10 bg-transparent text-zinc-500 hover:text-white hover:border-white/20 flex flex-col gap-2">
                                            <Plus className="w-8 h-8" />
                                            <span>Adicionar Card de Conduta</span>
                                        </Button>
                                    )}
                                </div>
                            </div>
                        </TabsContent>

                        <TabsContent value="info" className="animate-in fade-in slide-in-from-bottom-4 duration-500">
                            <div className="space-y-6">
                                {/* Ficha do Prestador */}
                                <Card className="bg-zinc-900/50 border-white/5 backdrop-blur-md overflow-hidden relative">
                                    <div className="absolute top-0 right-0 w-32 h-32 bg-purple-500/5 blur-[80px]" />
                                    <div className="absolute bottom-0 left-0 w-32 h-32 bg-blue-500/5 blur-[80px]" />

                                    <CardHeader className="border-b border-white/5 bg-white/[0.02] py-4">
                                        <div className="flex items-center justify-between">
                                            <div className="flex items-center gap-3">
                                                <div className="p-2 rounded-lg bg-zinc-800 border border-white/10">
                                                    <FileText className="w-5 h-5 text-purple-400" />
                                                </div>
                                                <div>
                                                    <CardTitle className="text-lg font-bold text-white tracking-tight leading-none">Ficha do Prestador</CardTitle>
                                                    <p className="text-[10px] uppercase font-bold text-zinc-500 tracking-widest mt-1">Dados Funcionais & Registro</p>
                                                </div>
                                            </div>
                                            <div className="flex items-center gap-2">
                                                {!isEditing && (
                                                    <Button
                                                        variant="ghost"
                                                        size="sm"
                                                        onClick={() => {
                                                            if (isEditingFicha) {
                                                                handleSaveFicha();
                                                            } else {
                                                                setIsEditingFicha(true);
                                                            }
                                                        }}
                                                        className={`h-8 w-8 p-0 rounded-lg hover:bg-white/10 ${isEditingFicha ? 'bg-emerald-500/20 text-emerald-400 hover:bg-emerald-500/30' : 'text-zinc-400 hover:text-white'}`}
                                                    >
                                                        {isEditingFicha ? <Check className="w-4 h-4" /> : <Edit3 className="w-4 h-4" />}
                                                    </Button>
                                                )}
                                                <Badge variant="outline" className={`px-3 py-1 border-white/10 ${selectedStaff.staffDetails?.conductStatus === 'green' ? 'bg-emerald-500/10 text-emerald-400 border-emerald-500/20' :
                                                    selectedStaff.staffDetails?.conductStatus === 'yellow' ? 'bg-amber-500/10 text-amber-400 border-amber-500/20' :
                                                        'bg-red-500/10 text-red-500 border-red-500/20'
                                                    }`}>
                                                    {selectedStaff.staffDetails?.conductStatus === 'green' ? 'Regular' :
                                                        selectedStaff.staffDetails?.conductStatus === 'yellow' ? 'Em Observação' : 'Crítico'}
                                                </Badge>
                                            </div>
                                        </div>
                                    </CardHeader>

                                    <CardContent className="p-0">
                                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 border-b border-white/5">
                                            <div className="p-6 border-r border-white/5 space-y-1">
                                                <span className="text-[10px] uppercase font-black text-zinc-500 flex items-center gap-1.5"><Users className="w-3 h-3" /> Nome Completo</span>
                                                <p className="text-white font-bold tracking-tight">{selectedStaff.name}</p>
                                            </div>
                                            <div className="p-6 border-r border-white/5 space-y-1">
                                                <span className="text-[10px] uppercase font-black text-zinc-500 flex items-center gap-1.5"><Shield className="w-3 h-3" /> Codinome</span>
                                                <p className="text-white font-bold tracking-tight">{selectedStaff.staffDetails?.codename || 'N/A'}</p>
                                            </div>
                                            <div className="p-6 border-r border-white/5 space-y-1">
                                                <span className="text-[10px] uppercase font-black text-zinc-500 flex items-center gap-1.5"><Hash className="w-3 h-3" /> Discord ID</span>
                                                <div className="flex items-center gap-2 group/copy">
                                                    <p className="text-white font-mono text-sm">{selectedStaff.discordId}</p>
                                                    <button
                                                        onClick={() => { navigator.clipboard.writeText(selectedStaff.discordId); toast.success('ID copiado!'); }}
                                                        className="opacity-0 group-hover/copy:opacity-100 transition-opacity p-1 rounded bg-white/5 hover:bg-white/10"
                                                    >
                                                        <Copy className="w-3 h-3 text-zinc-400" />
                                                    </button>
                                                </div>
                                            </div>
                                            <div className="p-6 space-y-1">
                                                <span className="text-[10px] uppercase font-black text-zinc-500 flex items-center gap-1.5"><MapPin className="w-3 h-3" /> Status do Sistema</span>
                                                <div className="flex items-center gap-2">
                                                    <div className={`w-2 h-2 rounded-full ${selectedStaff.status === 'online' ? 'bg-emerald-500 shadow-[0_0_8px_rgba(16,185,129,0.5)]' : 'bg-zinc-600'}`} />
                                                    <p className="text-white text-sm font-bold capitalize">{selectedStaff.status || 'Offline'}</p>
                                                </div>
                                            </div>
                                        </div>

                                        <div className="grid grid-cols-1 md:grid-cols-3 bg-white/[0.01]">
                                            <div className="p-6 border-r border-white/5 space-y-3">
                                                <span className="text-[10px] uppercase font-black text-purple-400 flex items-center gap-1.5"><Building2 className="w-3 h-3" /> Função</span>
                                                {isEditing || isEditingFicha ? (
                                                    <Input
                                                        value={editForm.mainFunction}
                                                        onChange={e => setEditForm({ ...editForm, mainFunction: e.target.value })}
                                                        className="bg-black/40 border-white/10 text-white h-10 font-medium"
                                                        placeholder="Ex: Supervisor de Atendimento"
                                                    />
                                                ) : (
                                                    <p className="text-white text-lg font-bold tracking-tight">{editForm.mainFunction || 'Não informada'}</p>
                                                )}
                                            </div>
                                            <div className="p-6 border-r border-white/5 space-y-3">
                                                <span className="text-[10px] uppercase font-black text-blue-400 flex items-center gap-1.5"><Calendar className="w-3 h-3" /> Data de Início</span>
                                                {isEditing || isEditingFicha ? (
                                                    <Input
                                                        type="date"
                                                        value={editForm.startDate ? new Date(editForm.startDate).toISOString().split('T')[0] : ''}
                                                        onChange={e => setEditForm({ ...editForm, startDate: e.target.value })}
                                                        className="bg-black/40 border-white/10 text-white h-10 font-medium"
                                                    />
                                                ) : (
                                                    <p className="text-white text-lg font-bold tracking-tight">
                                                        {editForm.startDate ? format(new Date(editForm.startDate), 'dd/MM/yyyy') : 'N/A'}
                                                    </p>
                                                )}
                                            </div>
                                            <div className="p-6 space-y-3">
                                                <span className="text-[10px] uppercase font-black text-amber-400 flex items-center gap-1.5"><Clock className="w-3 h-3" /> Turno / Disponibilidade</span>
                                                {isEditing || isEditingFicha ? (
                                                    <Input
                                                        value={editForm.shift}
                                                        onChange={e => setEditForm({ ...editForm, shift: e.target.value })}
                                                        className="bg-black/40 border-white/10 text-white h-10 font-medium"
                                                        placeholder="Ex: T1 (14h - 22h)"
                                                    />
                                                ) : (
                                                    <p className="text-white text-lg font-bold tracking-tight">{editForm.shift || 'Não informado'}</p>
                                                )}
                                            </div>
                                        </div>
                                    </CardContent>
                                </Card>

                                {/* Monthly Feedback Block (Moved from Overview) */}
                                <Card className="bg-blue-950/10 border-blue-500/10 border backdrop-blur-md">
                                    <CardHeader className="flex flex-row items-center justify-between pb-4 border-b border-white/5">
                                        <div className="flex items-center gap-3">
                                            <div className="p-2 rounded-lg bg-blue-500/10 border border-blue-500/20">
                                                <MessageSquare className="w-5 h-5 text-blue-400" />
                                            </div>
                                            <div>
                                                <h3 className="font-bold text-blue-100 tracking-tight">Feedback Mensal</h3>
                                                <p className="text-[10px] uppercase font-bold text-zinc-500 tracking-widest mt-1">Acompanhamento de Desempenho</p>
                                            </div>
                                        </div>
                                        <Select value={selectedFeedbackMonth} onValueChange={setSelectedFeedbackMonth}>
                                            <SelectTrigger className="w-[180px] bg-zinc-900/50 border-white/10 h-10 text-xs text-white rounded-xl focus:ring-0">
                                                <SelectValue />
                                            </SelectTrigger>
                                            <SelectContent className="bg-zinc-900 border-zinc-800 text-white">
                                                {getMonthOptions().map(m => (
                                                    <SelectItem key={m} value={m} className="focus:bg-blue-600 focus:text-white">{m}</SelectItem>
                                                ))}
                                            </SelectContent>
                                        </Select>
                                    </CardHeader>
                                    <CardContent className="pt-6">
                                        {isEditing || isEditingFicha ? (
                                            <Textarea
                                                value={getCurrentMonthFeedback()}
                                                onChange={e => handleFeedbackChange(e.target.value)}
                                                className="bg-black/40 border-white/10 min-h-[150px] text-white focus:ring-blue-500/50"
                                                placeholder={`Escreva o feedback de ${selectedFeedbackMonth}...`}
                                            />
                                        ) : (
                                            <div className="bg-black/20 rounded-xl p-4 border border-white/5">
                                                <p className="text-zinc-300 text-sm whitespace-pre-wrap leading-relaxed">
                                                    {getCurrentMonthFeedback() || "Sem feedback registrado para este mês."}
                                                </p>
                                            </div>
                                        )}
                                    </CardContent>
                                </Card>

                                {/* Férias, Licenças & Ausências */}
                                <div className="space-y-4">
                                    <div className="flex items-center justify-between px-2">
                                        <div className="flex items-center gap-3">
                                            <div className="p-2 rounded-lg bg-amber-500/10 border border-amber-500/20">
                                                <Calendar className="w-4 h-4 text-amber-500" />
                                            </div>
                                            <div>
                                                <h3 className="font-bold text-white leading-none">Férias, Licenças & Histórico</h3>
                                                <p className="text-xs text-zinc-500 mt-1">Registros de períodos ausentes e observações especiais</p>
                                            </div>
                                        </div>
                                        {(isEditing || isEditingFicha) && (
                                            <Button onClick={() => addCard('infoCards')} size="sm" className="bg-white/5 hover:bg-white/10 border border-white/10 text-white gap-2">
                                                <Plus className="w-4 h-4" /> Novo Registro
                                            </Button>
                                        )}
                                    </div>

                                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                                        {editForm.infoCards?.map(card => (
                                            <Card key={card.id} className="bg-zinc-900/30 border-white/5 relative group hover:border-white/20 transition-all overflow-hidden">
                                                <div className="absolute top-0 left-0 w-full h-[2px] bg-gradient-to-r from-purple-500/50 to-blue-500/50 opacity-0 group-hover:opacity-100 transition-opacity" />
                                                <CardHeader className="pb-2">
                                                    <div className="flex justify-between items-start">
                                                        {isEditing || isEditingFicha ? (
                                                            <Input
                                                                value={card.title}
                                                                onChange={e => updateCard('infoCards', card.id, 'title', e.target.value)}
                                                                className="h-7 font-bold bg-transparent border-none p-0 text-white placeholder:text-zinc-500 focus-visible:ring-0 text-sm"
                                                                placeholder="Título"
                                                            />
                                                        ) : (
                                                            <h4 className="font-bold text-white text-sm tracking-tight">{card.title || 'Cartão sem título'}</h4>
                                                        )}
                                                        {(isEditing || isEditingFicha) && (
                                                            <button
                                                                onClick={() => removeCard('infoCards', card.id)}
                                                                className="p-1 rounded bg-red-500/10 text-red-500 opacity-0 group-hover:opacity-100 transition-all"
                                                            >
                                                                <Trash2 className="w-3 h-3" />
                                                            </button>
                                                        )}
                                                    </div>
                                                </CardHeader>
                                                <CardContent>
                                                    {isEditing || isEditingFicha ? (
                                                        <Textarea
                                                            value={card.description}
                                                            onChange={e => updateCard('infoCards', card.id, 'description', e.target.value)}
                                                            className="min-h-[80px] text-xs bg-black/40 border-white/10 text-zinc-300 focus-visible:ring-purple-500/50"
                                                            placeholder="Detalhes..."
                                                        />
                                                    ) : (
                                                        <p className="text-xs text-zinc-400 leading-relaxed whitespace-pre-wrap">{card.description || 'Sem descrição'}</p>
                                                    )}
                                                </CardContent>
                                            </Card>
                                        ))}

                                        {(!editForm.infoCards || editForm.infoCards.length === 0) && !isEditing && (
                                            <div className="col-span-full py-12 flex flex-col items-center justify-center border border-dashed border-white/5 rounded-2xl bg-white/[0.01]">
                                                <Calendar className="w-8 h-8 text-zinc-800 mb-3" />
                                                <p className="text-zinc-600 text-sm font-medium">Nenhum registro de ausência ou licença.</p>
                                            </div>
                                        )}
                                    </div>
                                </div>

                                {/* GESTÃO DE CIDADES (NOVO) */}
                                <div className="space-y-4 mt-8">
                                    <div className="flex items-center justify-between px-2">
                                        <div className="flex items-center gap-3">
                                            <div className="p-2 rounded-lg bg-emerald-500/10 border border-emerald-500/20">
                                                <LayoutGrid className="w-4 h-4 text-emerald-500" />
                                            </div>
                                            <div>
                                                <h3 className="font-bold text-white leading-none">Gestão de Cidades & Permissões</h3>
                                                <p className="text-xs text-zinc-500 mt-1">Configure o acesso do staff às diferentes cidades do grupo</p>
                                            </div>
                                        </div>
                                    </div>

                                    <Card className="bg-zinc-900/30 border-white/5 overflow-hidden">
                                        <div className="p-1 bg-amber-500/10 border-b border-white/5 text-[9px] uppercase font-black text-amber-500/80 text-center tracking-[0.2em] py-1.5 flex items-center justify-center gap-2">
                                            <AlertTriangle className="w-3 h-3" /> ATENÇÃO: Alterações impactam permissões em tempo real
                                        </div>
                                        <CardContent className="p-6">
                                            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                                                {[
                                                    'Capital',
                                                    'Morada do Valley',
                                                    'Valley Norte',
                                                    'Valley Sul'
                                                ].map(cityName => {
                                                    const isMember = selectedStaff.cities?.some(c => c.city === cityName);
                                                    return (
                                                        <div
                                                            key={cityName}
                                                            onClick={() => toggleCity(cityName)}
                                                            className={`
                                                                relative cursor-pointer group transition-all duration-300 p-4 rounded-2xl border 
                                                                ${isMember ? 'bg-emerald-500/10 border-emerald-500/30 shadow-lg shadow-emerald-500/5' : 'bg-black/20 border-white/5 hover:border-white/20'}
                                                            `}
                                                        >
                                                            <div className="flex flex-col items-center text-center gap-3">
                                                                <div className={`p-3 rounded-xl transition-all duration-500 ${isMember ? 'bg-emerald-500 text-white' : 'bg-zinc-800 text-zinc-500'}`}>
                                                                    <Building2 className="w-5 h-5" />
                                                                </div>
                                                                <div>
                                                                    <div className={`text-sm font-bold ${isMember ? 'text-white' : 'text-zinc-500'}`}>{cityName}</div>
                                                                    <div className="mt-1 flex items-center justify-center gap-1">
                                                                        {isMember ? (
                                                                            <span className="flex items-center text-[9px] uppercase font-bold text-emerald-500">
                                                                                <Check className="w-2.5 h-2.5 mr-1" /> Ativo
                                                                            </span>
                                                                        ) : (
                                                                            <span className="text-[9px] uppercase font-bold text-zinc-600">Inativo</span>
                                                                        )}
                                                                    </div>
                                                                </div>
                                                            </div>
                                                            {isMember && (
                                                                <div className="absolute top-3 right-3">
                                                                    <Badge className="bg-emerald-500/20 text-emerald-400 border-0 p-1 rounded-full">
                                                                        <Check className="w-3 h-3" />
                                                                    </Badge>
                                                                </div>
                                                            )}
                                                        </div>
                                                    );
                                                })}
                                            </div>

                                            <div className="mt-6 p-4 bg-zinc-950/50 rounded-xl border border-white/5">
                                                <p className="text-[11px] text-zinc-500 leading-relaxed italic text-center">
                                                    * Ao desativar uma cidade, este staff perderá a habilidade de realizar ações administrativas (lançamentos, moderação de tickets, etc) vinculadas especificamente àquela localidade.
                                                </p>
                                            </div>
                                        </CardContent>
                                    </Card>
                                </div>
                            </div>
                        </TabsContent>

                        <TabsContent value="stats" className="animate-in fade-in slide-in-from-bottom-4 duration-500">
                            <div className="space-y-6">
                                {/* Header & Month Selector */}
                                <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                                    <div className="flex items-center gap-3">
                                        <div className="p-3 rounded-xl bg-pink-500/10 border border-pink-500/20">
                                            <BarChart2 className="w-6 h-6 text-pink-500" />
                                        </div>
                                        <div>
                                            <h3 className="text-xl font-bold text-white">Performance Mensal</h3>
                                            <p className="text-zinc-400 text-sm">Acompanhamento detalhado por base</p>
                                        </div>
                                    </div>
                                    <Select value={selectedFeedbackMonth} onValueChange={setSelectedFeedbackMonth}>
                                        <SelectTrigger className="w-full md:w-[260px] bg-black/40 border-white/10 h-12 text-white font-medium rounded-xl backdrop-blur-md focus:ring-0 focus:border-purple-500/50 transition-all hover:bg-white/5 data-[state=open]:border-purple-500/50 data-[state=open]:bg-white/5">
                                            <div className="flex items-center gap-3">
                                                <div className="p-1.5 rounded-lg bg-zinc-800/80">
                                                    <Calendar className="w-4 h-4 text-zinc-300" />
                                                </div>
                                                <span className="text-sm tracking-wide"><SelectValue /></span>
                                            </div>
                                        </SelectTrigger>
                                        <SelectContent className="bg-zinc-950/95 border-zinc-800 text-white backdrop-blur-xl rounded-xl shadow-2xl p-1">
                                            {getMonthOptions().map(m => (
                                                <SelectItem
                                                    key={m}
                                                    value={m}
                                                    className="rounded-lg focus:bg-purple-600 focus:text-white cursor-pointer py-2.5 px-2 my-0.5 font-medium transition-colors"
                                                >
                                                    {m}
                                                </SelectItem>
                                            ))}
                                        </SelectContent>
                                    </Select>

                                    <Button
                                        onClick={() => setLaunchModalOpen(true)}
                                        className="bg-purple-600 hover:bg-purple-700 text-white font-bold gap-2 h-12 rounded-xl px-6 shadow-lg shadow-purple-900/20 hover:shadow-purple-900/40 transition-all"
                                    >
                                        <PenTool className="w-4 h-4" />
                                        Lançamento
                                    </Button>
                                </div>

                                {/* Highlights Cards */}
                                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                                    <Card className="bg-gradient-to-br from-emerald-900/40 to-black border-white/5 relative overflow-hidden">
                                        <div className="absolute right-0 top-0 p-3 opacity-10"><Crown className="w-16 h-16" /></div>
                                        <CardContent className="p-4">
                                            <h4 className="text-xs font-bold text-emerald-400 uppercase tracking-wider mb-1">Melhor Mês</h4>
                                            <div className="text-2xl font-black text-white">{highlights.bestMonth}</div>
                                            <p className="text-xs text-zinc-500 mt-1">{highlights.maxTotal} atendimentos</p>
                                        </CardContent>
                                    </Card>

                                    <Card className="bg-gradient-to-br from-red-900/40 to-black border-white/5 relative overflow-hidden">
                                        <div className="absolute right-0 top-0 p-3 opacity-10"><TrendingDown className="w-16 h-16" /></div>
                                        <CardContent className="p-4">
                                            <h4 className="text-xs font-bold text-red-400 uppercase tracking-wider mb-1">Pior Mês</h4>
                                            <div className="text-2xl font-black text-white">{highlights.worstMonth}</div>
                                            <p className="text-xs text-zinc-500 mt-1">{highlights.minTotal} atendimentos</p>
                                        </CardContent>
                                    </Card>

                                    <Card className="bg-gradient-to-br from-purple-900/40 to-black border-white/5 relative overflow-hidden">
                                        <div className="absolute right-0 top-0 p-3 opacity-10"><Award className="w-16 h-16" /></div>
                                        <CardContent className="p-4">
                                            <h4 className="text-xs font-bold text-purple-400 uppercase tracking-wider mb-1">Foco Principal</h4>
                                            <div className="text-2xl font-black text-white">{highlights.mainCategory}</div>
                                            <p className="text-xs text-zinc-500 mt-1">Categoria mais frequente</p>
                                        </CardContent>
                                    </Card>
                                </div>

                                {/* City Stats Grid */}
                                <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">
                                    {['Capital', 'Morada do Valley', 'Valley Norte', 'Valley Sul'].map(city => {
                                        const cityStats = getMonthStats().cities?.[city] || { support: 0, coupon: 0, complaint: 0, other: 0 };

                                        // City Styles
                                        const cityStyle = getCityStyle(city);
                                        const CityIcon = cityStyle.icon;

                                        return (
                                            <Card key={city} className="bg-[#09090b] border-white/5 overflow-hidden group">
                                                <div className={`h-1 w-full bg-gradient-to-r ${cityStyle.gradient}`} />
                                                <CardHeader className="pb-2">
                                                    <div className="flex items-center gap-3">
                                                        <div className={`p-2 rounded-lg bg-gradient-to-br ${cityStyle.gradient} shadow-lg`}>
                                                            <CityIcon className="w-4 h-4 text-white" />
                                                        </div>
                                                        <CardTitle className="text-lg font-bold text-white">{city}</CardTitle>
                                                    </div>
                                                </CardHeader>
                                                <CardContent>
                                                    <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
                                                        {[
                                                            { label: 'Suporte', key: 'support', color: 'text-blue-400', bg: 'bg-blue-500/10 border-blue-500/20' },
                                                            { label: 'Cupom', key: 'coupon', color: 'text-emerald-400', bg: 'bg-emerald-500/10 border-emerald-500/20' },
                                                            { label: 'Denúncia', key: 'complaint', color: 'text-red-400', bg: 'bg-red-500/10 border-red-500/20' },
                                                            { label: 'Outros', key: 'other', color: 'text-zinc-400', bg: 'bg-zinc-800 border-zinc-700' }
                                                        ].map(stat => (
                                                            <div key={stat.key} className={`p-3 rounded-xl border ${stat.bg} flex flex-col items-center justify-center text-center transition-all hover:scale-105`}>
                                                                <span className="text-[10px] uppercase font-bold text-zinc-500 mb-1">{stat.label}</span>
                                                                {isEditing ? (
                                                                    <input
                                                                        type="number"
                                                                        min="0"
                                                                        className={`w-full bg-transparent text-center font-black text-xl outline-none border-b border-transparent focus:border-white/20 transition-colors ${stat.color} [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none`}
                                                                        value={cityStats[stat.key]}
                                                                        onChange={(e) => handleStatsChange(city, stat.key, parseInt(e.target.value) || 0)}
                                                                    />
                                                                ) : (
                                                                    <span className={`text-xl font-black ${stat.color}`}>{cityStats[stat.key]}</span>
                                                                )}
                                                            </div>
                                                        ))}
                                                    </div>
                                                </CardContent>
                                            </Card>
                                        );
                                    })}
                                </div>

                                {/* Stats Notes Card */}
                                <Card className="bg-zinc-900/50 border-white/5 overflow-hidden">
                                    <div className="h-1 w-full bg-gradient-to-r from-pink-500 to-purple-500" />
                                    <CardHeader className="flex flex-row items-center justify-between pb-2">
                                        <CardTitle className="text-white flex items-center gap-2">
                                            <div className="p-2 rounded-lg bg-pink-500/10">
                                                <FileText className="w-5 h-5 text-pink-400" />
                                            </div>
                                            Análise & Observações
                                        </CardTitle>
                                        {!isEditing && (
                                            <Button
                                                variant="ghost"
                                                size="sm"
                                                onClick={() => {
                                                    if (isEditingNotes) {
                                                        handleSaveNotes();
                                                    } else {
                                                        setIsEditingNotes(true);
                                                    }
                                                }}
                                                className={`h-8 w-8 p-0 rounded-lg hover:bg-white/10 ${isEditingNotes ? 'bg-emerald-500/20 text-emerald-400 hover:bg-emerald-500/30' : 'text-zinc-400 hover:text-white'}`}
                                            >
                                                {isEditingNotes ? <Check className="w-4 h-4" /> : <Edit3 className="w-4 h-4" />}
                                            </Button>
                                        )}
                                    </CardHeader>
                                    <CardContent>
                                        {isEditing || isEditingNotes ? (
                                            <div className="space-y-4 animate-in fade-in zoom-in-95 duration-300">
                                                <Input
                                                    placeholder="Título da observação..."
                                                    value={getMonthStats().notes?.title || ''}
                                                    onChange={e => handleStatsNoteChange('title', e.target.value)}
                                                    className="bg-black/20 border-white/10 text-white font-bold h-11 focus-visible:ring-purple-500/50"
                                                />
                                                <Textarea
                                                    placeholder="Descreva a análise deste mês..."
                                                    value={getMonthStats().notes?.description || ''}
                                                    onChange={e => handleStatsNoteChange('description', e.target.value)}
                                                    className="bg-black/20 border-white/10 text-zinc-300 min-h-[120px] focus-visible:ring-purple-500/50 leading-relaxed"
                                                />
                                            </div>
                                        ) : (
                                            <div className="space-y-3 animate-in fade-in zoom-in-95 duration-300">
                                                <h4 className="text-lg font-bold text-white tracking-tight">{getMonthStats().notes?.title || 'Sem observações'}</h4>
                                                <p className="text-zinc-400 leading-relaxed whitespace-pre-wrap text-sm">
                                                    {getMonthStats().notes?.description || 'Nenhuma análise registrada para este período.'}
                                                </p>
                                            </div>
                                        )}
                                    </CardContent>
                                </Card>

                                {/* Launch Modal */}
                                <Dialog open={launchModalOpen} onOpenChange={setLaunchModalOpen}>
                                    <DialogContent className="bg-zinc-950/95 backdrop-blur-xl border-white/10 text-white">
                                        <DialogHeader>
                                            <DialogTitle>Lançamento de Dados ({selectedFeedbackMonth})</DialogTitle>
                                        </DialogHeader>
                                        <div className="space-y-4 py-4">
                                            <div>
                                                <label className="text-xs font-bold text-zinc-500 uppercase mb-1 block">Cidade Base</label>
                                                <Select
                                                    value={launchForm.city}
                                                    onValueChange={val => setLaunchForm({ ...launchForm, city: val })}
                                                >
                                                    <SelectTrigger className="bg-black/40 border-white/10 text-white"><SelectValue /></SelectTrigger>
                                                    <SelectContent className="bg-zinc-900 border-zinc-800 text-white">
                                                        <SelectItem value="Capital">Capital</SelectItem>
                                                        <SelectItem value="Morada do Valley">Morada do Valley</SelectItem>
                                                        <SelectItem value="Valley Norte">Valley Norte</SelectItem>
                                                        <SelectItem value="Valley Sul">Valley Sul</SelectItem>
                                                    </SelectContent>
                                                </Select>
                                            </div>

                                            <div className="grid grid-cols-2 gap-4">
                                                <div>
                                                    <label className="text-xs font-bold text-blue-400 uppercase mb-1 block">Suporte</label>
                                                    <Input
                                                        type="number"
                                                        value={launchForm.support}
                                                        onChange={e => setLaunchForm({ ...launchForm, support: parseInt(e.target.value) || 0 })}
                                                        className="bg-black/40 border-white/10 text-white"
                                                    />
                                                </div>
                                                <div>
                                                    <label className="text-xs font-bold text-emerald-400 uppercase mb-1 block">Cupom</label>
                                                    <Input
                                                        type="number"
                                                        value={launchForm.coupon}
                                                        onChange={e => setLaunchForm({ ...launchForm, coupon: parseInt(e.target.value) || 0 })}
                                                        className="bg-black/40 border-white/10 text-white"
                                                    />
                                                </div>
                                                <div>
                                                    <label className="text-xs font-bold text-red-400 uppercase mb-1 block">Denúncia</label>
                                                    <Input
                                                        type="number"
                                                        value={launchForm.complaint}
                                                        onChange={e => setLaunchForm({ ...launchForm, complaint: parseInt(e.target.value) || 0 })}
                                                        className="bg-black/40 border-white/10 text-white"
                                                    />
                                                </div>
                                                <div>
                                                    <label className="text-xs font-bold text-zinc-400 uppercase mb-1 block">Outros</label>
                                                    <Input
                                                        type="number"
                                                        value={launchForm.other}
                                                        onChange={e => setLaunchForm({ ...launchForm, other: parseInt(e.target.value) || 0 })}
                                                        className="bg-black/40 border-white/10 text-white"
                                                    />
                                                </div>
                                            </div>

                                            <Button onClick={handleLaunchSave} className="w-full bg-purple-600 hover:bg-purple-700 text-white font-bold h-12 mt-4">
                                                <Save className="w-4 h-4 mr-2" /> Salvar Lançamento
                                            </Button>
                                        </div>
                                    </DialogContent>
                                </Dialog>
                            </div>

                            <div className="mt-6 p-4 rounded-xl bg-blue-900/10 border border-blue-500/20 flex items-start gap-4">
                                <AlertCircle className="w-5 h-5 text-blue-400 shrink-0 mt-0.5" />
                                <div>
                                    <h4 className="font-bold text-blue-400 text-sm">Informação do Sistema</h4>
                                    <p className="text-xs text-zinc-400 mt-1">
                                        Os dados estatísticos são atualizados manualmente pelos analistas de qualidade ou automaticamente através da integração com o bot de tickets (em breve).
                                        Certifique-se de salvar as alterações após editar os valores.
                                    </p>
                                </div>
                            </div>
                        </TabsContent>
                    </Tabs>

                    {/* Modals placed globally */}
                    <Dialog open={alignmentsModalOpen} onOpenChange={setAlignmentsModalOpen}>
                        <DialogContent className="bg-zinc-950/95 backdrop-blur-xl border-white/10 text-white sm:max-w-md">
                            <DialogHeader>
                                <DialogTitle className="flex items-center gap-2">
                                    <div className="p-2 rounded-lg bg-amber-500/10"><Target className="w-5 h-5 text-amber-500" /></div>
                                    Novo Alinhamento
                                </DialogTitle>
                            </DialogHeader>
                            <div className="space-y-4 py-4">
                                <div className="space-y-2">
                                    <label className="text-[10px] uppercase font-bold text-zinc-500 ml-1">Assunto / Pauta</label>
                                    <Input placeholder="Ex: Feedback de Conduta" value={newAlignment.topic} onChange={e => setNewAlignment({ ...newAlignment, topic: e.target.value })} className="bg-black/40 border-white/10 focus:border-amber-500/50" />
                                </div>
                                <div className="space-y-2">
                                    <label className="text-[10px] uppercase font-bold text-zinc-500 ml-1">Plano de Ação / Resumo</label>
                                    <Textarea placeholder="Descreva o que foi conversado e as ações combinadas..." value={newAlignment.actions} onChange={e => setNewAlignment({ ...newAlignment, actions: e.target.value })} className="bg-black/40 border-white/10 min-h-[120px] focus:border-amber-500/50" />
                                </div>
                                <Button onClick={handleAddAlignment} className="w-full bg-amber-600 hover:bg-amber-700 text-white font-bold h-11">
                                    <Save className="w-4 h-4 mr-2" /> Salvar Alinhamento
                                </Button>
                            </div>
                        </DialogContent>
                    </Dialog>

                    <Dialog open={notesModalOpen} onOpenChange={setNotesModalOpen}>
                        <DialogContent className="bg-zinc-950/95 backdrop-blur-xl border-white/10 text-white sm:max-w-md">
                            <DialogHeader>
                                <DialogTitle className="flex items-center gap-2">
                                    <div className="p-2 rounded-lg bg-purple-500/10"><FileText className="w-5 h-5 text-purple-500" /></div>
                                    Nova Entrada na Timeline
                                </DialogTitle>
                            </DialogHeader>
                            <div className="space-y-4 py-4">
                                <div className="grid grid-cols-2 gap-4">
                                    <div className="space-y-2 col-span-2">
                                        <label className="text-[10px] uppercase font-bold text-zinc-500 ml-1">Título da Nota</label>
                                        <Input placeholder="Título" value={newNote.title} onChange={e => setNewNote({ ...newNote, title: e.target.value })} className="bg-black/40 border-white/10 focus:border-purple-500/50" />
                                    </div>
                                    <div className="space-y-2 col-span-2">
                                        <label className="text-[10px] uppercase font-bold text-zinc-500 ml-1">Categoria / Tipo</label>
                                        <Select value={newNote.category} onValueChange={val => setNewNote({ ...newNote, category: val })}>
                                            <SelectTrigger className="bg-black/40 border-white/10 h-11">
                                                <SelectValue placeholder="Selecione o tipo..." />
                                            </SelectTrigger>
                                            <SelectContent className="bg-zinc-900 border-zinc-800 text-white">
                                                <SelectItem value="Geral">📝 Nota Geral</SelectItem>
                                                <SelectItem value="Elogio">⭐ Elogio / Destaque</SelectItem>
                                                <SelectItem value="Feedback">💬 Feedback Técnico</SelectItem>
                                                <SelectItem value="Treinamento">📚 Treinamento / Evolução</SelectItem>
                                                <SelectItem value="Disciplinar">🛡️ Medida Disciplinar</SelectItem>
                                                <SelectItem value="Advertência Verbal">⚠️ Advertência Verbal</SelectItem>
                                                <SelectItem value="Advertência Escrita">🚫 Advertência Escrita</SelectItem>
                                                <SelectItem value="Suspensão">❌ Suspensão</SelectItem>
                                            </SelectContent>
                                        </Select>
                                    </div>
                                </div>
                                <div className="space-y-2">
                                    <label className="text-[10px] uppercase font-bold text-zinc-500 ml-1">Descrição Detalhada</label>
                                    <Textarea placeholder="Escreva os detalhes aqui..." value={newNote.description} onChange={e => setNewNote({ ...newNote, description: e.target.value })} className="bg-black/40 border-white/10 min-h-[120px] focus:border-purple-500/50" />
                                </div>
                                <Button onClick={handleAddNote} className="w-full bg-purple-600 hover:bg-purple-700 text-white font-bold h-11">
                                    <Plus className="w-4 h-4 mr-2" /> Adicionar à Timeline
                                </Button>
                            </div>
                        </DialogContent>
                    </Dialog>

                </div >
            </div >
        );
    }

    // --- VIEW 2: LISTA DE STAFF DA CIDADE ---
    return (
        <div className="space-y-8 animate-in fade-in duration-500 max-w-7xl mx-auto py-8">
            <div className="flex flex-col md:flex-row md:items-center justify-between gap-6 px-4">
                <div className="flex items-center gap-6">
                    <Button
                        variant="ghost"
                        onClick={() => setSelectedCity(null)}
                        className="h-12 w-12 rounded-2xl bg-zinc-900 border border-white/10 hover:bg-white/5 hover:scale-105 transition-all shadow-lg"
                    >
                        <ArrowLeft className="w-5 h-5 text-white" />
                    </Button>
                    <div>
                        <h1 className="text-3xl font-black text-white uppercase tracking-tight drop-shadow-md">
                            Equipe {selectedCity}
                        </h1>
                        <p className="text-zinc-400 font-medium">Gestão de Performance e Perfil</p>
                    </div>
                </div>

                <div className="relative w-full md:w-80 group">
                    <div className="absolute -inset-0.5 bg-gradient-to-r from-purple-600 to-blue-600 rounded-xl opacity-20 group-hover:opacity-40 transition duration-500 blur"></div>
                    <div className="relative">
                        <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-500 group-hover:text-white transition-colors" />
                        <Input
                            placeholder="Buscar staff..."
                            value={searchTerm}
                            onChange={e => setSearchTerm(e.target.value)}
                            className="pl-11 bg-zinc-900 border-white/10 text-white rounded-xl h-12 focus:ring-0 focus:border-white/20 transition-all placeholder:text-zinc-600"
                        />
                    </div>
                </div>
            </div>

            {filteredStaff.length > 0 ? (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6 px-4">
                    {filteredStaff.map(staff => {
                        const codename = staff.staffDetails?.codename
                            || staff.staffDetails?.businessData?.codename
                            || staff.cities?.find(c => c.characterName && c.characterName !== 'N/A' && c.characterName !== staff.name)?.characterName
                            || staff.name;

                        return (
                            <motion.div
                                key={staff.discordId}
                                layoutId={staff.discordId}
                                initial={{ opacity: 0, scale: 0.95 }}
                                animate={{ opacity: 1, scale: 1 }}
                                whileHover={{ y: -5 }}
                                transition={{ duration: 0.2 }}
                            >
                                <Card
                                    onClick={() => setSelectedStaff(staff)}
                                    className="bg-zinc-900/40 border-white/5 hover:border-white/20 cursor-pointer transition-all group overflow-hidden relative shadow-lg hover:shadow-2xl hover:bg-zinc-900/60"
                                >
                                    <div className={`absolute top-0 left-0 w-1 h-full ${staff.staffDetails?.contractSigned ? 'bg-gradient-to-b from-emerald-500 to-emerald-700' : 'bg-gradient-to-b from-amber-500 to-amber-700'}`} />

                                    <CardContent className="p-5 flex items-center gap-5">
                                        <div className="w-14 h-14 rounded-2xl bg-zinc-800 border border-white/5 overflow-hidden shrink-0 shadow-inner relative group-hover:ring-2 ring-white/10 transition-all">
                                            {staff.avatar ? (
                                                <img src={`https://cdn.discordapp.com/avatars/${staff.discordId}/${staff.avatar}.png`} alt="" className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110" />
                                            ) : (
                                                <div className="w-full h-full flex items-center justify-center bg-zinc-800">
                                                    <Users className="w-6 h-6 text-zinc-600 group-hover:text-zinc-400 transition-colors" />
                                                </div>
                                            )}
                                        </div>
                                        <div className="min-w-0 flex-1">
                                            <h4 className="font-bold text-white text-base truncate group-hover:text-transparent group-hover:bg-clip-text group-hover:bg-gradient-to-r group-hover:from-white group-hover:to-zinc-400 transition-all mb-1">
                                                {codename}
                                            </h4>
                                            <Badge variant="outline" className="text-[10px] uppercase border-white/10 text-zinc-500 bg-zinc-900/50">
                                                {getRoleBadge(staff.accessLevel)}
                                            </Badge>
                                        </div>
                                        <div className="opacity-0 group-hover:opacity-100 transition-opacity absolute right-4 top-1/2 -translate-y-1/2">
                                            <ArrowLeft className="w-5 h-5 text-white rotate-180" />
                                        </div>
                                    </CardContent>
                                </Card>
                            </motion.div>
                        );
                    })}
                </div>
            ) : (
                <div className="flex flex-col items-center justify-center py-24 border-2 border-dashed border-white/5 rounded-3xl bg-white/[0.01] mx-4 animate-in fade-in">
                    <div className="w-20 h-20 bg-zinc-900/50 rounded-full flex items-center justify-center mb-6 shadow-xl border border-white/5">
                        <Users className="w-8 h-8 text-zinc-600" />
                    </div>
                    <h3 className="text-xl font-bold text-white mb-2">Nenhum membro encontrado</h3>
                    <p className="text-zinc-500 font-medium">Não há staff registrado nesta cidade ou com este filtro.</p>
                </div>
            )}
        </div>
    );
};

export default StaffReview;
